import React, { Suspense, useCallback, useEffect, useMemo, useState, lazy } from "react";
import { Link, useParams, useLocation, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { PLUGIN_IDS, isPluginEnabled } from "./plugins/registry";
import { summaryGroup, phaseLabelForActiveSubphase } from "./projectGridColumns";
import {
  getPhaseDetailShellClassName,
  displayPhaseForRow,
  buildAdvancePhasePatch,
  nextPhaseLabelForRow,
  phaseFlowForRow,
  flowIndexForPhase,
  resolvePhaseStyleKey,
  PHASE_CELL_BACKGROUND,
} from "./projectPhaseDisplay";
import ProjectPayloadView, { humanizeFieldKey } from "./ProjectPayloadView";
import ProjectCommentsPanel from "./ProjectCommentsPanel";
import {
  buildPhaseCompleteEmailPayload,
  describePhaseEmailResult,
} from "./plugins/phaseEmailTriggerPayload";
import {
  fetchPhaseTriggerEmailPreview,
  phaseCompleteEmailUsesPreview,
  sendPhaseTriggerEmail,
} from "./plugins/phaseEmailTriggerApi";
import PhaseCompleteEmailModal from "./plugins/PhaseCompleteEmailModal";
import {
  emitMarketplaceRewardFromResponse,
  describePhaseMarketplaceRewardResult,
} from "./plugins/marketplaceRewards";
import "./App.css";
import { GLOBAL_PHASES, EXPANSION_PHASE_FLOW } from "./projectPhaseDisplay";
import { ACF, isCvinExpansion, currentSubphaseForRow, formatExpansionWorkOrderForDisplay } from "./projectGridColumns";
import { readSubsegmentSummaryFromAcf } from "./plugins/expansionProjectAcf";
import {
  canManageEngineeringDrawings,
  getViewerEmailFromStorage,
  hasEffectiveAdminPrivileges,
  hasViewerOperationalAdminAccess,
} from "./plugins/userMatrixStorage";
import { canAccessProjectComments } from "./plugins/appChromeAccess";
import { viewerIsContractor } from "./plugins/contractorProjectAccess";
import {
  EngineeringMaterialsCatalogEditor,
  MaterialCatalogSelect,
  findCatalogMaterial,
  unitLabelForMaterialName,
  useEngineeringMaterialsCatalog,
} from "./plugins/engineeringMaterialsCatalog";
import {
  EngineeringMaterialsSummary,
  collectPermitMaterialsFromDomains,
  normalizeMaterialRows,
} from "./plugins/engineeringMaterialsTotals";
import {
  uploadProjectFieldFile,
  clearProjectFieldFile,
} from "./plugins/projectFieldFilesApi";
import {
  fileDisplayLabelFromStoredValue,
  fileUrlFromStoredValue,
  openProjectFileUrl,
} from "./plugins/CommercialFieldFileDisplay";
import { isProjectFieldFileContentUrl } from "./projectFieldFileAuth";
import {
  downloadFilledWorkOrderExcel,
  projectUsesGeneratedWorkOrderExcel,
} from "./plugins/workOrderExcelDownload";
import CommercialProjectSectionPanel from "./plugins/CommercialProjectSectionPanel";
import CommercialSectionReadView from "./plugins/CommercialSectionReadView";
import { LandingPermittingTab } from "./plugins/PermitsSectionPanel";
import {
  buildCommercialPatchBody,
  countCommercialPendingEdits,
  readCommercialDomains,
  readCommercialDomainsForRow,
  repeaterToArray,
} from "./plugins/commercialProjectAcf";
import { isCommercialWorkOrderGroupParent } from "./plugins/commercialWorkOrderGroup";
import { mergeCommercialFileFieldsFromServer } from "./plugins/commercialProjectFiles";
import { PROJECT_LANDING_TAB_TO_DOMAIN, COMMERCIAL_DOMAINS, computeCircuitId, departmentIncludes, departmentIncludesFiber, normalizeDepartments, normalizeMulti, serviceSpeedLabel, isCommercialIncidentFlag, INCIDENT_TYPE_OPTIONS, normalizeSelectOptions } from "./plugins/commercialProjectSchema";
import PhaseSchedulePrompt from "./plugins/PhaseSchedulePrompt";
import ReportedFootagePrompt from "./plugins/ReportedFootagePrompt";
import ExpansionFootageProgressBar from "./plugins/ExpansionFootageProgressBar";
import { computeFootageProgressForProject, formatFootageNumber } from "./plugins/expansionProgressUtils";
import ExpansionWorkOrderMapPreview from "./plugins/ExpansionWorkOrderMapPreview";
import {
  EXPANSION_DOMAIN_SCHEMAS,
  EXPANSION_LANDING_TAB_TO_DOMAIN,
  EXPANSION_SUMMARY_DOMAIN,
  expansionPhaseApplies,
  subphaseSelectOptionsForExpansionPhase,
  visibleExpansionLandingTabs,
} from "./plugins/expansionProjectSchema";
import {
  buildExpansionPatchBody,
  countExpansionPendingEdits,
  readExpansionEditDomains,
} from "./plugins/expansionProjectForms";
import { maybeSyncExpansionPhaseToAgol } from "./plugins/expansionAgolPhaseSync";
import { fetchProjectContractFields } from "./plugins/contractsApi";
import { fetchProjectInterconnectFields } from "./plugins/interconnectsApi";
import { fetchProjectPermitCoverage } from "./plugins/permitCoverageApi";
import { expansionProjectHasCdt } from "./plugins/interconnectsUtils";
import {
  canEditExpansionRestrictedFields,
} from "./plugins/expansionFieldAccess";
import {
  canEditCommercialOverviewFields,
  canEditEngineeringTabFields,
  canEditConstructionTabUploads,
  canEditInspectorDailyProgressReports,
  canEditAccountingTabFields,
  canEditNetworkingTabFields,
  canEditFiberTabFields,
  canEditOspEngineeringSiteWalkFiles,
  canEditPermittingTabFields,
  canViewNetworkingTabFiberFiles,
  isAccountantViewer,
  isSplicerViewer,
} from "./plugins/commercialRoleAccess";
import {
  filterLandingTabsForViewer,
  editableTabsForPersona,
  primaryEditTabForPersona,
  resolveViewerLandingPersona,
} from "./plugins/projectLandingTabAccess";
import { confirmAndArchiveDeleteProjects } from "./plugins/projectArchiveDelete";
import "./plugins/workflowSchedule.css";
import {
  buildAccountantCloseCommercialProjectPatch,
  canShowAccountantCloseProjectButton,
  canShowPhaseCompleteButton,
  commercialPhaseApplies,
  fieldHasUpload,
  formatSubphaseLabel,
  installationRowIsSchedulable,
  isNonRevenueOrCapitalProject,
} from "./plugins/commercialSubphases";
import { notifyNetworkingInstallationEmails } from "./plugins/networkingInstallationApi";
import { LandingWoApprovalKpiCard } from "./plugins/LandingWoApprovalKpiCard";
import {
  formatContractorsForRole,
  overlayContractDerivedConstruction,
} from "./plugins/contractsDerivedFields";
import { formatDateOnlyDisplay } from "./plugins/dateOnlyDisplay";
import { useMobileViewport } from "./plugins/useMobileViewport";
import "./plugins/projectLandingMobile.css";

const ServiceLocationMapPreview = lazy(() => import("./ServiceLocationMapPreview"));

const PROJECT_LANDING_TABS = [
  { id: "summary", label: "Overview" },
  { id: "engineering", label: "Engineering" },
  { id: "permitting", label: "Permitting" },
  { id: "construction", label: "Construction" },
  { id: "fiber", label: "Fiber" },
  { id: "networking", label: "Networking" },
  { id: "accounting", label: "Accounting" },
];

function landingTabVisible(tabId, commercialDomains) {
  const summary = commercialDomains?.[COMMERCIAL_DOMAINS.SUMMARY] || {};
  if (tabId === "summary") return true;
  if (tabId === "engineering") return departmentIncludes(summary, "Engineering");
  if (tabId === "permitting") return departmentIncludes(summary, "Engineering");
  if (tabId === "construction") return departmentIncludes(summary, "Construction");
  if (tabId === "fiber") return departmentIncludesFiber(summary);
  if (tabId === "networking") return departmentIncludes(summary, "Networking");
  if (tabId === "accounting") return departmentIncludes(summary, "Accounting");
  return true;
}

function visibleLandingTabs(commercialDomains) {
  if (!commercialDomains) return PROJECT_LANDING_TABS;
  return PROJECT_LANDING_TABS.filter((tab) => landingTabVisible(tab.id, commercialDomains));
}

function collectEngineeringPermitMaterials(commercialDomains) {
  return collectPermitMaterialsFromDomains(commercialDomains);
}

function networkingCircuitDetailRows(commercialDomains) {
  const summary = commercialDomains?.[COMMERCIAL_DOMAINS.SUMMARY] || {};
  const net = commercialDomains?.[COMMERCIAL_DOMAINS.NETWORKING] || {};
  const fmt = (v) => (v == null || String(v).trim() === "" ? null : String(v).trim());
  const rows = [];
  const conn = normalizeMulti(net.connection ?? summary.connection_type);
  if (conn.length) rows.push({ label: "Connection", value: conn.join(", ") });
  const speed = fmt(serviceSpeedLabel(net.service_speed ?? summary.service_speed));
  if (speed) rows.push({ label: "Service speed", value: speed });
  const ipsRaw = fmt(net.ips) ?? fmt(summary.number_of_ips);
  if (ipsRaw) rows.push({ label: "IPs", value: ipsRaw.startsWith("/") ? ipsRaw : `/${ipsRaw}` });
  const handoff = fmt(net.handoff ?? summary.handoff_type);
  if (handoff) rows.push({ label: "Handoff", value: handoff });
  const circuitId = computeCircuitId(summary, net);
  if (circuitId) rows.push({ label: "Circuit ID", value: circuitId });
  const aLoc = fmt(summary.a_location);
  if (aLoc) rows.push({ label: "A location", value: aLoc });
  const zLoc = fmt(summary.z_location);
  if (zLoc) rows.push({ label: "Z location", value: zLoc });
  return rows;
}

/** @returns {"materials"|"materials-empty"|"circuit"|null} */
function resolveSummaryMiddlePanelMode(domains, summaryDomainKey = COMMERCIAL_DOMAINS.SUMMARY) {
  const summary = domains?.[summaryDomainKey] || {};
  const hasEng = departmentIncludes(summary, "Engineering");
  const hasNet = departmentIncludes(summary, "Networking");
  const materials = domains ? collectEngineeringPermitMaterials(domains) : [];
  const hideMaterials = viewerIsContractor();
  if (!hideMaterials && (hasEng || materials.length)) return "materials";
  if (hasNet) return "circuit";
  if (hideMaterials) return null;
  return "materials-empty";
}

function SummaryMiddlePanel({ domains, summaryDomainKey = COMMERCIAL_DOMAINS.SUMMARY, mode }) {
  const resolved = mode ?? resolveSummaryMiddlePanelMode(domains, summaryDomainKey);
  if (!resolved) return null;

  if (resolved === "circuit") {
    const circuitRows = networkingCircuitDetailRows(domains);
    return (
      <div className="project-summary-panel project-summary-panel-circuit">
        <h3 className="project-summary-panel-title">Circuit details</h3>
        {circuitRows.length === 0 ? (
          <div className="project-landing-empty-state">
            <span className="project-landing-empty-icon" aria-hidden>
              <LandingIcon name="circuit" />
            </span>
            <p className="project-detail-muted">No circuit details entered yet.</p>
          </div>
        ) : (
          <ul className="project-landing-detail-list">
            {circuitRows.map((row) => (
              <li key={row.label} className="project-landing-detail-item">
                <span className="project-landing-detail-icon" aria-hidden>
                  <LandingIcon name="circuit" />
                </span>
                <div className="project-landing-detail-copy">
                  <span className="project-landing-detail-label">{row.label}</span>
                  <span className="project-landing-detail-value">{row.value}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  if (resolved === "materials-empty") {
    return (
      <div className="project-summary-panel project-summary-panel-materials">
        <h3 className="project-summary-panel-title">Material summary</h3>
        <p className="project-detail-muted">Not applicable for this project&apos;s departments.</p>
      </div>
    );
  }

  const materials = domains ? collectEngineeringPermitMaterials(domains) : [];
  return (
    <div className="project-summary-panel project-summary-panel-materials">
      <h3 className="project-summary-panel-title">Material summary</h3>
      <EngineeringMaterialsSummary rows={materials} />
    </div>
  );
}

function LandingIcon({ name, className = "" }) {
  const common = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
  const icons = {
    pin: <path d="M12 21s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11z" />,
    calendar: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
    user: <><path d="M20 21a8 8 0 0 0-16 0" /><circle cx="12" cy="8" r="4" /></>,
    folder: <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />,
    file: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></>,
    dollar: <><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7H14a3.5 3.5 0 0 1 0 7H6" /></>,
    plug: <><path d="M12 22v-5" /><path d="M9 8V2M15 8V2" /><path d="M18 8v2a6 6 0 0 1-12 0V8z" /></>,
    permit: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M9 15l2 2 4-4" /></>,
    list: <><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" /></>,
    shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
    map: <><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" /><line x1="8" y1="2" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="22" /></>,
    check: <path d="M20 6 9 17l-5-5" />,
    circle: <circle cx="12" cy="12" r="9" strokeDasharray="4 3" />,
    pencil: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></>,
    chevron: <path d="M9 18l6-6-6-6" />,
    circuit: <><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M9 9h6v6H9zM9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 15h3M20 9h3M20 15h3" /></>,
  };
  return (
    <svg className={`project-landing-icon ${className}`.trim()} aria-hidden {...common}>
      {icons[name] || null}
    </svg>
  );
}

/** Single-line audit / table cells (no multi-line JSON blocks). */
function formatAuditCellValue(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") {
    try {
      const s = JSON.stringify(v);
      return s.length > 140 ? `${s.slice(0, 137)}…` : s;
    } catch {
      return String(v);
    }
  }
  return String(v);
}

function domainDisplayTitle(domainKey) {
  const k = String(domainKey);
  if (/\s/.test(k)) return k;
  return humanizeFieldKey(k);
}

/** Short plain-text summary of an ACF domain payload for the dashboard cards (not full field dump). */
function payloadPreviewSummary(payload) {
  if (payload == null || payload === "") return "No data yet.";
  if (typeof payload !== "object" || Array.isArray(payload)) {
    const s = formatAuditCellValue(payload);
    return s.length > 220 ? `${s.slice(0, 217)}…` : s;
  }
  const keys = Object.keys(payload);
  if (keys.length === 0) return "Empty section.";
  const parts = [];
  for (const k of keys.slice(0, 8)) {
    const v = payload[k];
    const label = humanizeFieldKey(k);
    if (v === null || v === undefined) continue;
    if (typeof v === "string") {
      const t = v.trim();
      if (!t) continue;
      parts.push(t.length > 60 ? `${label}: ${t.slice(0, 57)}…` : `${label}: ${t}`);
    } else if (typeof v === "boolean") {
      parts.push(`${label}: ${v ? "Yes" : "No"}`);
    } else if (typeof v === "number" && Number.isFinite(v)) {
      parts.push(`${label}: ${v}`);
    } else if (Array.isArray(v)) {
      parts.push(`${label}: ${v.length} item${v.length === 1 ? "" : "s"}`);
    } else {
      parts.push(`${label}: …`);
    }
  }
  if (parts.length === 0) return `${keys.length} field${keys.length === 1 ? "" : "s"}`;
  return parts.join(" · ");
}

function DomainPreviewCard({ domainKey, payload }) {
  return (
    <div className="project-dashboard-domain-card">
      <h3 className="project-dashboard-domain-title">{domainDisplayTitle(domainKey)}</h3>
      <p className="project-dashboard-domain-preview">{payloadPreviewSummary(payload)}</p>
    </div>
  );
}

function acfGroupsJson(row) {
  const j = row?.acf_groups_json;
  return j && typeof j === "object" ? j : {};
}

function engineeringAcfGroup(row) {
  const j = acfGroupsJson(row);
  return j[ACF.ENG] ?? j.engineering_test_2 ?? null;
}

function fiberAcfGroup(row) {
  const j = acfGroupsJson(row);
  return j[ACF.FIBER] ?? j.fiber_test_2 ?? null;
}

function parseLandingNumeric(v) {
  if (v == null || v === "") return null;
  const n = Number(String(v).replace(/[$,]/g, "").trim());
  return Number.isFinite(n) ? n : null;
}

function formatLandingDollars(amount) {
  if (amount == null || !Number.isFinite(amount)) return null;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** MRC + NRC amortized over term (monthly total). */
function landingAmortizedMrcTag(row, commercialDomains) {
  const acct = commercialDomains?.accounting_details || {};
  const mrc =
    parseLandingNumeric(row?.mrc) ??
    parseLandingNumeric(acct.mrc) ??
    parseLandingNumeric(commercialDomains?.project_summary?.mrc);
  const nrc =
    parseLandingNumeric(row?.non_recurring_charge) ?? parseLandingNumeric(acct.nrc);
  const term =
    parseLandingNumeric(row?.term_in_months) ?? parseLandingNumeric(acct.term);
  if (mrc == null && nrc == null) return "";
  let monthly = mrc ?? 0;
  if (nrc != null && term != null && term > 0) {
    monthly += nrc / term;
  }
  const formatted = formatLandingDollars(monthly);
  return formatted ? `MRC: ${formatted}` : "";
}

function landingPhaseIndex(label, row) {
  if (row && isCvinExpansion(row)) {
    return flowIndexForPhase(EXPANSION_PHASE_FLOW, label);
  }
  if (!label) return -1;
  const n = String(label).trim().toLowerCase();
  const idx = GLOBAL_PHASES.findIndex((p) => p.toLowerCase() === n);
  if (idx >= 0) return idx;
  if (n === "drafting" || n === "engineering") {
    return GLOBAL_PHASES.findIndex((p) => p === "Drafting");
  }
  return -1;
}

function projectProgressPercent(row, commercialDomains) {
  if (isCvinExpansion(row)) {
    const phase = phaseLabelForActiveSubphase(row) || row?.phase_label || "";
    const rIdx = flowIndexForPhase(EXPANSION_PHASE_FLOW, phase);
    if (rIdx < 0) return 0;
    return Math.round((rIdx / Math.max(1, EXPANSION_PHASE_FLOW.length - 1)) * 100);
  }
  const phase = phaseLabelForActiveSubphase(row) || row?.phase_label || "";
  const idx = landingPhaseIndex(phase, row);
  if (idx < 0) return null;
  if (isRenewalProject(row, commercialDomains)) {
    const flow = phaseFlowForRow(row);
    const phase = phaseLabelForActiveSubphase(row) || row?.phase_label || "";
    const rIdx = flowIndexForPhase(flow, phase);
    if (rIdx < 0) return 0;
    return Math.round((rIdx / Math.max(1, flow.length - 1)) * 100);
  }
  const denom = Math.max(1, GLOBAL_PHASES.length - 1);
  return Math.round((idx / denom) * 100);
}

/** Onsite / site contact from commercial project_summary. */
function landingSiteContact(commercialDomains) {
  const s = commercialDomains?.project_summary || {};
  const fmt = (v) => (v == null || v === "" ? null : String(v).trim());
  return {
    name: fmt(s.onsite_contact_name),
    email: fmt(s.onsite_contact_email),
    phone: fmt(s.onsite_contact_number),
  };
}

function landingDepartments(row, commercialDomains) {
  if (isCvinExpansion(row)) {
    const sub = readSubsegmentSummaryFromAcf(row?.acf_groups_json);
    return normalizeDepartments(sub.departments);
  }
  const s = commercialDomains?.project_summary || summaryGroup(row) || {};
  return normalizeDepartments(s.departments ?? s.department_involvement);
}

function isRenewalProject(row, commercialDomains) {
  const s = commercialDomains?.project_summary || {};
  const raw = s.subtype ?? row?.subtype;
  return normalizeMulti(raw).some((v) => String(v).trim().toLowerCase() === "renewal");
}

/** Whether a workflow phase applies given departments / renewal subtype. */
function phaseAppliesToProject(phase, ctx) {
  return commercialPhaseApplies(phase, ctx);
}

function phaseScheduleRows(row, commercialDomains) {
  const expansion = isCvinExpansion(row);
  const phases = expansion ? EXPANSION_PHASE_FLOW : GLOBAL_PHASES;
  const summary = expansion ? readSubsegmentSummaryFromAcf(row?.acf_groups_json) : summaryGroup(row);
  const subObj =
    summary && typeof summary === "object"
      ? expansion
        ? summary.subphases ?? summary.subphases_exp
        : summary.subphases
      : null;
  const activeLabel = phaseLabelForActiveSubphase(row);
  const activeIdx = expansion
    ? flowIndexForPhase(EXPANSION_PHASE_FLOW, activeLabel)
    : landingPhaseIndex(activeLabel, row);
  const departments = landingDepartments(row, commercialDomains);
  const isRenewal = isRenewalProject(row, commercialDomains);
  const scheduleCtx = { departments, isRenewal };
  const keyMap = {
    Sales: "sales_subphase",
    Finance: "finance_subphase",
    Drafting: "drafting_subphase",
    Permitting: "permitting_subphase",
    Construction: "construction_subphase",
    Fiber: "fiber_subphase",
    Networking: "networking_subphase",
    Accounting: "accounting_subphase",
  };
  return phases.map((phase, idx) => {
    const applies = expansion
      ? expansionPhaseApplies(phase, summary)
      : phaseAppliesToProject(phase, scheduleCtx);
    const key = keyMap[phase];
    const subRaw =
      subObj && key && subObj[key] != null
        ? String(subObj[key]).trim()
        : idx === activeIdx
          ? currentSubphaseForRow(row)
          : "";
    const sub = subRaw ? formatSubphaseLabel(subRaw) : "";
    if (!applies) {
      return { phase, subphase: "—", state: "na" };
    }
    let state = "planned";
    if (idx < activeIdx) state = "done";
    else if (idx === activeIdx) state = "active";
    const displaySub = state === "done" ? "Complete" : sub || "—";
    return { phase, subphase: displaySub, state };
  });
}

function scheduleBarTooltip(row) {
  if (row.state === "na") return "Not applicable";
  if (row.state === "done") return "Complete";
  if (row.subphase && row.subphase !== "—") return row.subphase;
  return "Not started";
}

/** Permitting section (`engineering_details.permits`): one row = one application; approved when permit file is uploaded. */
function permitMetrics(row) {
  const eng = engineeringAcfGroup(row);
  const permitRows = repeaterToArray(eng?.permits);
  if (!permitRows.length) {
    return { approved: 0, total: 0, items: [], pct: null };
  }
  const items = permitRows.map((p, index) => {
    const approved = fieldHasUpload(p?.permit);
    return {
      agency:
        p?.agency != null && String(p.agency).trim() !== ""
          ? String(p.agency).trim()
          : `Application ${index + 1}`,
      approved,
      submitted:
        p?.application_submitted_date != null ? String(p.application_submitted_date).trim() : "",
      hasApplication: fieldHasUpload(p?.permit_application),
    };
  });
  const approved = items.filter((i) => i.approved).length;
  const total = items.length;
  const pct = total > 0 ? Math.round((approved / total) * 100) : null;
  return { approved, total, items, pct };
}

const DONE_SUBPHASE_HINTS = ["complete", "completed", "billed", "invoiced", "closed", "done"];

function openSubphasesCount(row) {
  const summary = summaryGroup(row);
  if (!summary || typeof summary !== "object") return 0;
  const subObj = isCvinExpansion(row) ? summary.subphases_exp : summary.subphases;
  if (!subObj || typeof subObj !== "object") return 0;
  return Object.values(subObj).filter((v) => {
    const s = v != null ? String(v).trim().toLowerCase() : "";
    if (!s) return false;
    return !DONE_SUBPHASE_HINTS.some((h) => s.includes(h));
  }).length;
}

function formatLandingNumber(n) {
  if (n == null || !Number.isFinite(n)) return "—";
  return n.toLocaleString();
}

function LandingProgressRing({ percent, size = 72, complete = false }) {
  const p = percent != null ? Math.min(100, Math.max(0, percent)) : 0;
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (p / 100) * c;
  const stroke = complete || p >= 100 ? "#22c55e" : "#2563eb";
  return (
    <div className="project-kpi-ring" style={{ width: size, height: size }} aria-hidden>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth="6" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={stroke}
          strokeWidth="6"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <span className="project-kpi-ring-label" style={{ color: stroke }}>
        {percent != null ? `${p}%` : "—"}
      </span>
    </div>
  );
}

function LandingKpiBar({ pct, tone = "blue" }) {
  const w = pct != null ? `${Math.min(100, Math.max(0, pct))}%` : "0%";
  return (
    <div className="project-kpi-bar" role="presentation">
      <div className={`project-kpi-bar-fill project-kpi-bar-fill--${tone}`} style={{ width: w }} />
    </div>
  );
}

function trimLocationText(value) {
  if (value == null) return "";
  const s = String(value).trim();
  return s !== "" ? s : "";
}

/** Prefer project columns, then commercial summary ACF. */
function resolveCommercialMapLocations(projectRow, commercialDomains) {
  const summary = commercialDomains?.[COMMERCIAL_DOMAINS.SUMMARY] || {};
  const aLocation =
    trimLocationText(projectRow?.a_location) || trimLocationText(summary.a_location);
  const zLocation =
    trimLocationText(projectRow?.z_location) || trimLocationText(summary.z_location);
  const serviceLocation =
    trimLocationText(projectRow?.service_location) ||
    trimLocationText(summary.service_location);
  return {
    aLocation,
    zLocation,
    serviceLocation,
    hasAz: Boolean(aLocation && zLocation),
  };
}

function LandingMapPanel({
  projectRow,
  mapUrl,
  arcgisApiKey,
  useExpansionNetworkMap,
  commercialDomains,
}) {
  if (useExpansionNetworkMap) {
    if (!arcgisApiKey) {
      return (
        <p className="project-detail-muted">
          Set <span className="project-detail-kbd">REACT_APP_ARCGIS_API_KEY</span> at build time for the segment
          map.
        </p>
      );
    }
    const workOrder = projectRow?.work_order != null ? String(projectRow.work_order).trim() : "";
    return (
      <div className="project-map-preview project-map-preview--compact">
        <ExpansionWorkOrderMapPreview workOrder={workOrder} />
      </div>
    );
  }
  const { aLocation, zLocation, serviceLocation, hasAz } = resolveCommercialMapLocations(
    projectRow,
    commercialDomains
  );
  if (!hasAz && !serviceLocation) {
    return <p className="project-detail-muted">No service location available for map preview.</p>;
  }
  if (arcgisApiKey) {
    return (
      <div className="project-map-preview project-map-preview--compact">
        <Suspense
          fallback={
            <div className="project-map-esri-container project-map-esri-loading">Loading map…</div>
          }
        >
          <ServiceLocationMapPreview
            serviceLocation={serviceLocation}
            aLocation={hasAz ? aLocation : undefined}
            zLocation={hasAz ? zLocation : undefined}
            externalMapUrl={mapUrl || undefined}
          />
        </Suspense>
        {mapUrl ? (
          <p className="project-detail-muted project-detail-map-foot">
            <a href={mapUrl} target="_blank" rel="noopener noreferrer" className="project-detail-link-subtle">
              Open in ArcGIS Map Viewer
            </a>
          </p>
        ) : null}
      </div>
    );
  }
  if (mapUrl) {
    return (
      <div className="project-map-preview project-map-fallback-panel project-map-preview--compact">
        <p className="project-detail-muted">
          Set <span className="project-detail-kbd">REACT_APP_ARCGIS_API_KEY</span> at build time for an embedded
          map.
        </p>
        <a href={mapUrl} target="_blank" rel="noopener noreferrer" className="project-map-fallback-cta">
          Open in ArcGIS Map Viewer
        </a>
      </div>
    );
  }
  return <p className="project-detail-muted">No map available.</p>;
}

function contractDerivedFieldsPayload(contractDerivedConstruction) {
  if (!contractDerivedConstruction || typeof contractDerivedConstruction !== "object") return null;
  if (
    contractDerivedConstruction.fields &&
    typeof contractDerivedConstruction.fields === "object"
  ) {
    return contractDerivedConstruction.fields;
  }
  return contractDerivedConstruction;
}

function landingContractorLabel(construction, contractDerivedConstruction) {
  const constructionFromContract = formatContractorsForRole(
    contractDerivedConstruction,
    "construction"
  );
  const fiberFromContract = formatContractorsForRole(contractDerivedConstruction, "fiber");
  const merged = overlayContractDerivedConstruction(
    { [COMMERCIAL_DOMAINS.CONSTRUCTION]: construction || {} },
    contractDerivedFieldsPayload(contractDerivedConstruction)
  );
  const cons = merged?.[COMMERCIAL_DOMAINS.CONSTRUCTION] || construction || {};
  const constructionName =
    constructionFromContract ||
    String(cons.construction_contractor || cons.contractor || "").trim();
  const fiber =
    fiberFromContract || String(cons.fiber_contractor || cons.contractor_fiber || "").trim();
  if (constructionName && fiber && constructionName !== fiber) {
    return `${constructionName} / ${fiber}`;
  }
  return constructionName || fiber || null;
}

/** Construction / fiber contractors for expansion subsegment Overview → Project details. */
function expansionLandingDetailRows(overviewDomains, contractDerivedConstruction) {
  const construction = overviewDomains?.[COMMERCIAL_DOMAINS.CONSTRUCTION] || {};
  const constructionFromContract = formatContractorsForRole(
    contractDerivedConstruction,
    "construction"
  );
  const fiberFromContract = formatContractorsForRole(contractDerivedConstruction, "fiber");
  const merged = overlayContractDerivedConstruction(
    { [COMMERCIAL_DOMAINS.CONSTRUCTION]: construction },
    contractDerivedFieldsPayload(contractDerivedConstruction)
  );
  const cons = merged?.[COMMERCIAL_DOMAINS.CONSTRUCTION] || construction;
  const constructionLabel =
    constructionFromContract ||
    String(cons.construction_contractor || cons.contractor || "").trim() ||
    null;
  const fiberLabel =
    fiberFromContract ||
    String(cons.fiber_contractor || cons.contractor_fiber || "").trim() ||
    null;
  return [
    {
      icon: "user",
      label: "Construction contractor",
      value: constructionLabel || "—",
      alwaysShow: true,
    },
    {
      icon: "user",
      label: "Fiber contractor",
      value: fiberLabel || "—",
      alwaysShow: true,
    },
  ];
}

function MobileDailyProgressReports({ reports }) {
  const rows = repeaterToArray(reports);
  if (!rows.length) {
    return <p className="project-detail-muted">No daily progress reports yet.</p>;
  }
  const sorted = [...rows].sort((a, b) => {
    const da = a?.date != null ? String(a.date) : "";
    const db = b?.date != null ? String(b.date) : "";
    return db.localeCompare(da);
  });
  return (
    <ul className="project-mobile-landing-reports">
      {sorted.map((row, index) => {
        const dateLabel = row?.date ? formatDateOnlyDisplay(row.date) || String(row.date) : "—";
        const parts = [];
        const conduit = row?.conduit_footage;
        const fiber = row?.fiber_footage;
        const proofing = row?.proofing_footage;
        const vaults = row?.vaults;
        if (conduit != null && String(conduit).trim() !== "") {
          parts.push(`Conduit ${conduit} ft`);
        }
        if (fiber != null && String(fiber).trim() !== "") {
          parts.push(`Fiber ${fiber} ft`);
        }
        if (proofing != null && String(proofing).trim() !== "") {
          parts.push(`Proofing ${proofing} ft`);
        }
        if (vaults != null && String(vaults).trim() !== "") {
          parts.push(`${vaults} vault${Number(vaults) === 1 ? "" : "s"}`);
        }
        const contractor = row?.contractor != null ? String(row.contractor).trim() : "";
        const notes = row?.notes != null ? String(row.notes).trim() : "";
        return (
          <li key={index} className="project-mobile-landing-report-card">
            <div className="project-mobile-landing-report-head">
              <span>{dateLabel}</span>
              {contractor ? <span>{contractor}</span> : null}
            </div>
            {parts.length ? (
              <div className="project-mobile-landing-report-meta">{parts.join(" · ")}</div>
            ) : null}
            {notes ? <div className="project-mobile-landing-report-meta">{notes}</div> : null}
          </li>
        );
      })}
    </ul>
  );
}

function ProjectMobileLandingView({
  projectRow,
  domains,
  summaryDomainKey,
  mapUrl,
  arcgisApiKey,
  contractDerivedConstruction,
  isExpansion,
  projectId,
  getAuthHeaders,
  apiUrl,
  permitCoverage,
}) {
  const construction = domains?.[COMMERCIAL_DOMAINS.CONSTRUCTION] || {};
  const engineering = domains?.[COMMERCIAL_DOMAINS.ENGINEERING] || {};
  const contractor = landingContractorLabel(construction, contractDerivedConstruction);
  const reports =
    overlayContractDerivedConstruction(
      { [COMMERCIAL_DOMAINS.CONSTRUCTION]: construction },
      contractDerivedFieldsPayload(contractDerivedConstruction)
    )?.[COMMERCIAL_DOMAINS.CONSTRUCTION]?.daily_progress_reports ?? construction.daily_progress_reports;
  const permitDrawings = normalizeConstructionDrawings(
    engineering?.permit_files ?? engineeringAcfGroup(projectRow)?.permit_files
  );

  return (
    <div className="project-mobile-landing project-detail-tab-content">
      <section className="project-mobile-landing-section" aria-label="Contractor">
        <h2 className="project-mobile-landing-section-title">Contractor</h2>
        <p className="project-mobile-landing-contractor">{contractor || "—"}</p>
      </section>

      <section className="project-mobile-landing-section" aria-label="Location map">
        <h2 className="project-mobile-landing-section-title">Location map</h2>
        <LandingMapPanel
          projectRow={projectRow}
          mapUrl={mapUrl}
          arcgisApiKey={arcgisApiKey}
          useExpansionNetworkMap={isExpansion}
          commercialDomains={isExpansion ? null : domains}
        />
      </section>

      <section className="project-mobile-landing-section" aria-label="Reports">
        <h2 className="project-mobile-landing-section-title">Reports</h2>
        <MobileDailyProgressReports reports={reports} />
      </section>

      <section className="project-mobile-landing-section" aria-label="Permit plans">
        <h2 className="project-mobile-landing-section-title">Permit plans</h2>
        <EngineeringDrawingsSummary drawings={permitDrawings} />
      </section>

      <section className="project-mobile-landing-section" aria-label="Permits">
        <h2 className="project-mobile-landing-section-title">Permits</h2>
        <LandingPermittingTab
          domains={domains}
          summaryDomainKey={summaryDomainKey}
          projectId={projectId}
          getAuthHeaders={getAuthHeaders}
          apiUrl={apiUrl}
          permitCoverage={isExpansion ? permitCoverage : null}
        />
      </section>
    </div>
  );
}

function landingDetailRows(projectRow, commercialDomains) {
  const s = commercialDomains?.project_summary || {};
  const acct = commercialDomains?.accounting_details || {};
  const summaryForType = {
    ...s,
    type: s.type ?? s.work_order_type ?? projectRow?.project_type,
  };
  const nonRevenue = isNonRevenueOrCapitalProject(summaryForType);
  const isIncident = isCommercialIncidentFlag(s.incident ?? projectRow?.incident);
  const networkingWoExcel = projectUsesGeneratedWorkOrderExcel(s);
  const fmt = (v) => (v == null || v === "" ? null : String(v).trim());
  const mrcRaw =
    projectRow?.mrc != null && projectRow.mrc !== ""
      ? projectRow.mrc
      : acct.mrc != null && acct.mrc !== ""
        ? acct.mrc
        : s.mrc;
  const nrcRaw =
    projectRow?.non_recurring_charge != null && projectRow.non_recurring_charge !== ""
      ? projectRow.non_recurring_charge
      : acct.nrc != null && acct.nrc !== ""
        ? acct.nrc
        : null;
  const rows = [];
  if (!isIncident) {
    rows.push(
      nonRevenue
        ? {
            icon: "file",
            label: "Signed work order",
            kind: "file",
            value: s.signed_work_order,
            fieldKey: "signed_work_order",
            accept: ".pdf",
            alwaysShow: true,
          }
        : {
            icon: "file",
            label: "Service order",
            kind: "file",
            value: s.service_order,
            fieldKey: "service_order",
            accept: ".pdf",
            alwaysShow: true,
          },
      networkingWoExcel
        ? {
            icon: "file",
            label: "Work order Excel",
            kind: "generated_wo_excel",
            projectId: projectRow?.id,
            alwaysShow: true,
          }
        : {
            icon: "file",
            label: "Work order Excel template",
            kind: "file",
            value: s.work_order_excel_template,
            fieldKey: "work_order_excel_template",
            accept: ".xlsx,.xls",
            alwaysShow: true,
          },
      {
        icon: "pin",
        label: "Service location",
        value: fmt(projectRow?.service_location) || fmt(s.service_location),
      },
      { icon: "user", label: "Sales rep", value: fmt(projectRow?.sales_rep) || fmt(s.sales_rep) },
      {
        icon: "dollar",
        label: "MRC",
        value: formatLandingDollars(parseLandingNumeric(mrcRaw)),
      },
      {
        icon: "dollar",
        label: "NRC",
        value: formatLandingDollars(parseLandingNumeric(nrcRaw)),
      }
    );
  } else {
    rows.push({
      icon: "pin",
      label: "Service location",
      value: fmt(projectRow?.service_location) || fmt(s.service_location),
    });
  }
  if (isIncident) {
    const typeOpts = normalizeSelectOptions(INCIDENT_TYPE_OPTIONS);
    const typeRaw = fmt(s.incident_type);
    const typeLabel =
      typeOpts.find((o) => o.value === typeRaw)?.label || typeRaw;
    rows.push(
      { icon: "shield", label: "Incident", value: "Fiber hit / incident", alwaysShow: true },
      { icon: "list", label: "Incident type", value: typeLabel },
      { icon: "calendar", label: "Incident date", value: fmt(s.incident_date) },
      { icon: "pin", label: "Damage location", value: fmt(s.damage_location) },
      { icon: "user", label: "Responsible party", value: fmt(s.responsible_party) },
      { icon: "plug", label: "Impacted network", value: fmt(s.impacted_network) },
      { icon: "user", label: "Contractor", value: fmt(s.incident_contractor) },
      { icon: "user", label: "Contractor contact", value: fmt(s.incident_contractor_contact) },
      { icon: "file", label: "Incident notes", value: fmt(s.incident_notes) }
    );
    const photoFiles = incidentPhotoOverviewFiles(s.incident_photos);
    if (photoFiles.length) {
      rows.push({
        icon: "file",
        label: "Incident photos",
        kind: "multifile",
        files: photoFiles,
        alwaysShow: true,
      });
    }
  }
  return rows.filter((r) => r.alwaysShow || r.value);
}

/** Incident photo files from project_summary.incident_photos. */
function incidentPhotoOverviewFiles(raw) {
  const rows = Array.isArray(raw) ? raw : raw ? [raw] : [];
  const files = [];
  rows.forEach((row, idx) => {
    const url = fileUrlFromStoredValue(row);
    if (!url) return;
    const label =
      fileDisplayLabelFromStoredValue(row, {
        fieldKey: "incident_photos",
        accept: ".png,.jpeg,.jpg,.webp,.pdf",
      }) || `Incident photo ${idx + 1}`;
    files.push({ url, label });
  });
  return files;
}

function LandingDetailValue({ row }) {
  if (row.kind === "generated_wo_excel") {
    return (
      <button
        type="button"
        className="landing-detail-file-link"
        onClick={() => downloadFilledWorkOrderExcel(row.projectId)}
      >
        Download filled workbook
      </button>
    );
  }
  if (row.kind === "file") {
    const fileUrl = fileUrlFromStoredValue(row.value);
    if (!fileUrl) {
      return <span className="project-detail-muted">Not uploaded</span>;
    }
    const displayName = fileDisplayLabelFromStoredValue(row.value, {
      fieldKey: row.fieldKey,
      accept: row.accept,
    });
    return (
      <button
        type="button"
        className="landing-detail-file-link"
        onClick={() => openProjectFileUrl(fileUrl)}
      >
        {displayName}
      </button>
    );
  }
  if (row.kind === "multifile") {
    const files = Array.isArray(row.files) ? row.files : [];
    if (!files.length) {
      return <span className="project-detail-muted">Not uploaded</span>;
    }
    return (
      <ul className="project-landing-usa-ticket-list">
        {files.map((file, idx) => (
          <li key={`${file.url}-${idx}`}>
            <button
              type="button"
              className="landing-detail-file-link"
              onClick={() => openProjectFileUrl(file.url)}
            >
              {file.label}
            </button>
          </li>
        ))}
      </ul>
    );
  }
  return row.value;
}

/** 811 / USA ticket files from construction_details (view/download for any landing viewer). */
function usaTicketOverviewFiles(domains) {
  const raw = domains?.[COMMERCIAL_DOMAINS.CONSTRUCTION]?.usa_ticket;
  const rows = Array.isArray(raw) ? raw : raw ? [raw] : [];
  const files = [];
  rows.forEach((row, idx) => {
    const url = fileUrlFromStoredValue(row);
    if (!url) return;
    const label = fileDisplayLabelFromStoredValue(row, {
      fieldKey: "usa_ticket",
      accept: ".pdf",
    }) || `811 / USA ticket ${idx + 1}`;
    files.push({ url, label });
  });
  return files;
}

function UsaTicketFileLinks({ files, className = "" }) {
  if (!files?.length) return null;
  return (
    <ul className={`project-landing-usa-ticket-list ${className}`.trim()}>
      {files.map((file, idx) => (
        <li key={`${file.url}-${idx}`}>
          <button
            type="button"
            className="landing-detail-file-link"
            onClick={() => openProjectFileUrl(file.url)}
          >
            {file.label}
          </button>
        </li>
      ))}
    </ul>
  );
}

function LandingNotesPanel({
  notes,
  canEdit,
  saving,
  onSave,
}) {
  const initial = notes != null ? String(notes) : "";
  const [draft, setDraft] = useState(initial);
  const [dirty, setDirty] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    setDraft(notes != null ? String(notes) : "");
    setDirty(false);
    setSaveError("");
  }, [notes]);

  const save = async () => {
    if (!canEdit || typeof onSave !== "function" || !dirty) return;
    setSaveError("");
    try {
      await onSave(draft);
      setDirty(false);
    } catch (err) {
      setSaveError(err?.response?.data?.error || err?.message || "Could not save notes.");
    }
  };

  return (
    <div className="project-summary-panel project-summary-panel-notes">
      <div className="project-summary-panel-title-row">
        <h3 className="project-summary-panel-title">Notes</h3>
        {canEdit && dirty ? (
          <button
            type="button"
            className="project-landing-notes-save"
            disabled={saving}
            onClick={() => void save()}
          >
            {saving ? "Saving…" : "Save notes"}
          </button>
        ) : null}
      </div>
      {canEdit ? (
        <textarea
          className="project-landing-notes-input"
          value={draft}
          rows={4}
          disabled={saving}
          placeholder="Add project notes…"
          onChange={(e) => {
            setDraft(e.target.value);
            setDirty(true);
          }}
        />
      ) : (
        <p className={`project-landing-notes-text${initial.trim() ? "" : " project-detail-muted"}`}>
          {initial.trim() || "No notes yet."}
        </p>
      )}
      {saveError ? (
        <p className="project-detail-error" role="alert" style={{ margin: "8px 0 0" }}>
          {saveError}
        </p>
      ) : null}
    </div>
  );
}

function ProjectSummaryDashboard({
  projectRow,
  commercialDomains,
  overviewDomains,
  summaryDomainKey = COMMERCIAL_DOMAINS.SUMMARY,
  contractDerivedConstruction = null,
  mapUrl,
  arcgisApiKey,
  recentActivity,
  onOpenActivity,
  notesEditable = false,
  notesSaving = false,
  onSaveNotes,
}) {
  const isExpansion = !commercialDomains;
  const domains = overviewDomains || commercialDomains;
  const progress = projectProgressPercent(projectRow, commercialDomains);
  const footageProgress = isExpansion ? computeFootageProgressForProject(projectRow) : null;
  const siteContact = !isExpansion ? landingSiteContact(commercialDomains) : null;
  const permits = permitMetrics(projectRow);
  const openTasks = openSubphasesCount(projectRow);
  const schedule = phaseScheduleRows(projectRow, commercialDomains);
  const status =
    projectRow?.status != null && String(projectRow.status).trim() !== ""
      ? String(projectRow.status).trim()
      : "";
  const statusComplete = status.toLowerCase() === "complete";
  const detailRows = isExpansion
    ? expansionLandingDetailRows(domains, contractDerivedConstruction)
    : landingDetailRows(projectRow, commercialDomains);
  const usaTicketFiles = usaTicketOverviewFiles(domains);
  const summaryMiddleMode = resolveSummaryMiddlePanelMode(domains, summaryDomainKey);
  const activityDots = ["project-landing-activity-dot--blue", "project-landing-activity-dot--green", "project-landing-activity-dot--amber"];
  const notesValue =
    domains?.[summaryDomainKey]?.notes ??
    commercialDomains?.project_summary?.notes ??
    projectRow?.project_notes ??
    "";

  return (
    <div className="project-summary-dashboard project-detail-tab-content">
      <div className="project-summary-kpi-row">
        <div className="project-kpi-card">
          <h3 className="project-kpi-card-title">Project progress</h3>
          <div className="project-kpi-progress">
            <LandingProgressRing percent={progress} complete={statusComplete} />
            <div className="project-kpi-progress-meta">
              <span className="project-kpi-big">{progress != null ? `${progress}%` : "—"}</span>
              <span className="project-kpi-sub">Complete</span>
              {status ? <span className="project-kpi-note">Status: {status}</span> : null}
            </div>
          </div>
        </div>
        <div className="project-kpi-card">
          <div className="project-kpi-card-head">
            <span className="project-kpi-card-icon project-kpi-card-icon--blue">
              <LandingIcon name={isExpansion ? "plug" : "user"} />
            </span>
            <h3 className="project-kpi-card-title">
              {isExpansion ? "Installation progress" : "Site contact"}
            </h3>
          </div>
          {isExpansion ? (
            <>
              <span className="project-kpi-big">
                {footageProgress?.percentComplete != null
                  ? `${footageProgress.percentComplete}%`
                  : "—"}
              </span>
              <span className="project-kpi-sub">
                {footageProgress?.fiberDenominator != null
                  ? `Fiber ${formatFootageNumber(footageProgress.fiberCompleted)} of ${formatFootageNumber(footageProgress.fiberDenominator)} ft`
                  : footageProgress?.completedTotal != null && footageProgress.completedTotal > 0
                    ? `${formatFootageNumber(footageProgress.completedTotal)} ft installed`
                    : "No footage reported yet"}
                {footageProgress?.conduitDenominator != null
                  ? ` · Conduit ${formatFootageNumber(footageProgress.conduitCompleted)} of ${formatFootageNumber(footageProgress.conduitDenominator)} ft`
                  : ""}
              </span>
              <ExpansionFootageProgressBar projectRow={projectRow} height={10} />
            </>
          ) : (
            <>
              <span className="project-kpi-big project-kpi-contact-name">
                {siteContact?.name || "—"}
              </span>
              {siteContact?.phone ? (
                <a className="project-kpi-contact-link" href={`tel:${siteContact.phone}`}>
                  {siteContact.phone}
                </a>
              ) : null}
              {siteContact?.email ? (
                <a className="project-kpi-contact-link" href={`mailto:${siteContact.email}`}>
                  {siteContact.email}
                </a>
              ) : null}
              {!siteContact?.name && !siteContact?.phone && !siteContact?.email ? (
                <span className="project-kpi-sub">No contact on file</span>
              ) : null}
            </>
          )}
        </div>
        <div className="project-kpi-card">
          <div className="project-kpi-card-head">
            <span className="project-kpi-card-icon project-kpi-card-icon--green">
              <LandingIcon name="permit" />
            </span>
            <h3 className="project-kpi-card-title">Permits</h3>
          </div>
          <span className="project-kpi-big">{permits.total > 0 ? formatLandingNumber(permits.approved) : "—"}</span>
          <span className="project-kpi-sub">Approved</span>
          <LandingKpiBar pct={permits.pct} tone="green" />
          <span className="project-kpi-note">
            {permits.total > 0
              ? `${permits.approved} of ${permits.total} application${permits.total === 1 ? "" : "s"} approved`
              : "No permit applications"}
          </span>
          {!isExpansion && usaTicketFiles.length > 0 ? (
            <div className="project-kpi-usa-tickets">
              <span className="project-kpi-note project-kpi-usa-tickets-label">811 / USA tickets</span>
              <UsaTicketFileLinks files={usaTicketFiles} />
            </div>
          ) : null}
        </div>
        {!isExpansion ? (
          <LandingWoApprovalKpiCard summary={commercialDomains?.[COMMERCIAL_DOMAINS.SUMMARY]} />
        ) : (
        <div className="project-kpi-card">
          <div className="project-kpi-card-head">
            <span className="project-kpi-card-icon project-kpi-card-icon--amber">
              <LandingIcon name="list" />
            </span>
            <h3 className="project-kpi-card-title">Open subphases</h3>
          </div>
          <span className="project-kpi-big">{formatLandingNumber(openTasks)}</span>
          <span className="project-kpi-sub">In progress</span>
          <span className="project-kpi-note">Across workflow phases</span>
        </div>
        )}
      </div>

      <div
        className={`project-summary-mid-row${
          summaryMiddleMode ? "" : " project-summary-mid-row--wide-map"
        }`}
      >
        <div className="project-summary-panel project-summary-panel-details">
          <h3 className="project-summary-panel-title">Project details</h3>
          <ul className="project-landing-detail-list">
            {detailRows.map((row) => (
              <li key={row.label} className="project-landing-detail-item">
                <span className="project-landing-detail-icon" aria-hidden>
                  <LandingIcon name={row.icon} />
                </span>
                <div className="project-landing-detail-copy">
                  <span className="project-landing-detail-label">{row.label}</span>
                  <span className="project-landing-detail-value">
                    <LandingDetailValue row={row} />
                  </span>
                </div>
              </li>
            ))}
            {isExpansion && usaTicketFiles.length > 0 ? (
              <li className="project-landing-detail-item">
                <span className="project-landing-detail-icon" aria-hidden>
                  <LandingIcon name="file" />
                </span>
                <div className="project-landing-detail-copy">
                  <span className="project-landing-detail-label">811 / USA tickets</span>
                  <span className="project-landing-detail-value">
                    <LandingDetailValue
                      row={{ kind: "multifile", files: usaTicketFiles }}
                    />
                  </span>
                </div>
              </li>
            ) : null}
          </ul>
        </div>
        <SummaryMiddlePanel
          domains={domains}
          summaryDomainKey={summaryDomainKey}
          mode={summaryMiddleMode}
        />
        <div className="project-summary-panel project-summary-panel-map">
          <h3 className="project-summary-panel-title">
            <LandingIcon name="map" className="project-summary-panel-title-icon" />
            Project map
          </h3>
          <LandingMapPanel
            projectRow={projectRow}
            mapUrl={mapUrl}
            arcgisApiKey={arcgisApiKey}
            useExpansionNetworkMap={!commercialDomains}
            commercialDomains={commercialDomains}
          />
        </div>
      </div>

      <div
        className={`project-summary-bottom-row${!isExpansion ? " project-summary-bottom-row--with-notes" : ""}`}
      >
        <div className="project-summary-panel project-summary-panel-schedule">
          <h3 className="project-summary-panel-title">Phase schedule</h3>
          <ul className="project-summary-schedule">
            {schedule.map((row) => (
              <li
                key={row.phase}
                className={`project-summary-schedule-row project-summary-schedule-row--${row.state}`}
              >
                <span className="project-summary-schedule-phase">{row.phase}</span>
                <div
                  className="project-summary-schedule-track"
                  title={scheduleBarTooltip(row)}
                  aria-label={`${row.phase}: ${scheduleBarTooltip(row)}`}
                >
                  <div className="project-summary-schedule-fill" />
                </div>
                <span className="project-summary-schedule-sub">
                  {row.state === "na"
                    ? "Not applicable"
                    : row.state === "active"
                      ? row.subphase !== "—"
                        ? row.subphase
                        : "In progress"
                      : row.subphase}
                </span>
                <span className="project-summary-schedule-icon" aria-hidden>
                  {row.state === "done" ? (
                    <LandingIcon name="check" className="project-summary-schedule-icon-svg" />
                  ) : row.state === "active" ? (
                    <LandingIcon name="circle" className="project-summary-schedule-icon-svg" />
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </div>
        {!isExpansion ? (
          <LandingNotesPanel
            notes={notesValue}
            canEdit={notesEditable}
            saving={notesSaving}
            onSave={onSaveNotes}
          />
        ) : null}
        <div className="project-summary-panel project-summary-panel-activity">
          <div className="project-summary-panel-title-row">
            <h3 className="project-summary-panel-title">Recent activity</h3>
            {typeof onOpenActivity === "function" ? (
              <button
                type="button"
                className="project-landing-activity-link"
                onClick={onOpenActivity}
                aria-label="Open change history"
              >
                <LandingIcon name="chevron" />
              </button>
            ) : null}
          </div>
          {recentActivity.length === 0 ? (
            <p className="project-detail-muted">No recent changes recorded.</p>
          ) : (
            <ul className="project-summary-activity-list">
              {recentActivity.map((item, idx) => (
                <li key={item.key} className="project-summary-activity-item">
                  <span
                    className={`project-landing-activity-dot ${activityDots[idx % activityDots.length]}`}
                    aria-hidden
                  />
                  <div className="project-summary-activity-body">
                    <span className="project-summary-activity-field">{item.field}</span>
                    <span className="project-summary-activity-meta">
                      {item.user ? `${item.user} · ` : ""}
                      {item.date || "—"}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

const ENG_GROUP = ACF.ENG;
const INV_GROUP = ACF.INVENTORY;

function emptyConstructionDrawingRow() {
  return {
    permit_agency: "",
    permit_plan: "",
    permit_application: "",
    application_submitted_date: "",
    permit_number: "",
    permit: false,
    permit_received_date: "",
    expiration_date: "",
    changes_required: false,
  };
}

function normalizeConstructionDrawings(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((row) => {
    const r = row && typeof row === "object" ? row : {};
    return {
      permit_agency: r.permit_agency != null ? String(r.permit_agency) : "",
      permit_plan: r.permit_plan != null && r.permit_plan !== false ? r.permit_plan : "",
      permit_application:
        r.permit_application != null && r.permit_application !== false ? r.permit_application : "",
      application_submitted_date:
        r.application_submitted_date != null ? String(r.application_submitted_date) : "",
      permit_number: r.permit_number != null ? String(r.permit_number) : "",
      permit: r.permit === true,
      permit_received_date: r.permit_received_date != null ? String(r.permit_received_date) : "",
      expiration_date: r.expiration_date != null ? String(r.expiration_date) : "",
      changes_required: r.changes_required === true,
    };
  });
}

function emptyMaterialRow() {
  return { slug: "", item: "", quantity: "" };
}

function cloneAcfGroupsForPatch(row) {
  const j = row?.acf_groups_json;
  if (!j || typeof j !== "object") return {};
  return JSON.parse(JSON.stringify(j));
}

function fileNameFromEngField(value, fieldKey, accept) {
  return fileDisplayLabelFromStoredValue(value, { fieldKey, accept });
}

function engFieldFileUrl(value) {
  return fileUrlFromStoredValue(value);
}

function EngineeringDrawingFileControl({ projectId, fieldPath, value, getAuthHeaders, onUpdated, label }) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileUrl = engFieldFileUrl(value);
  const hasHttp = typeof fileUrl === "string" && /^https?:\/\//i.test(fileUrl);
  const isOurFile = hasHttp && isProjectFieldFileContentUrl(fileUrl);
  const fieldKey = Array.isArray(fieldPath) ? String(fieldPath[fieldPath.length - 1] || "") : "";
  const displayName = fileNameFromEngField(value, fieldKey, ".pdf");

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      await uploadProjectFieldFile(projectId, fieldPath, file, getAuthHeaders);
      onUpdated?.();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async () => {
    if (!window.confirm("Remove this file?")) return;
    setBusy(true);
    setError("");
    try {
      await clearProjectFieldFile(projectId, fieldPath, getAuthHeaders);
      onUpdated?.();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Remove failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="eng-drawing-file">
      <span className="eng-drawing-file-label">{label}</span>
      {hasHttp ? (
        <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="eng-drawing-file-link">
          {displayName}
        </a>
      ) : (
        <span className="project-detail-muted">No file</span>
      )}
      <input ref={inputRef} type="file" className="project-field-file-input" onChange={onFileChange} disabled={busy} />
      <div className="project-field-file-actions">
        <button type="button" className="project-field-file-btn" onClick={() => inputRef.current?.click()} disabled={busy}>
          {busy ? "…" : hasHttp ? "Replace" : "Upload"}
        </button>
        {hasHttp && isOurFile ? (
          <button type="button" className="project-field-file-btn danger" onClick={onRemove} disabled={busy}>
            Remove
          </button>
        ) : null}
      </div>
      {error ? <p className="project-field-file-error">{error}</p> : null}
    </div>
  );
}

function EngineeringDrawingsSummary({ drawings }) {
  if (!drawings.length) {
    return <p className="project-detail-muted">No construction drawings on file.</p>;
  }
  return (
    <ul className="eng-drawings-summary-list">
      {drawings.map((row, i) => {
        const approved = row.permit === true || String(row.permit_received_date || "").trim() !== "";
        const planUrl = engFieldFileUrl(row.permit_plan);
        return (
          <li key={i} className="eng-drawings-summary-card">
            <div className="eng-drawings-summary-head">
              <strong>{row.permit_agency || `Drawing ${i + 1}`}</strong>
              <span
                className={
                  approved
                    ? "project-summary-permit-badge project-summary-permit-badge--ok"
                    : "project-summary-permit-badge project-summary-permit-badge--pending"
                }
              >
                {approved ? "Issued" : row.application_submitted_date ? "Applied" : "Plan only"}
              </span>
            </div>
            {planUrl ? (
              <div className="eng-drawings-summary-line">
                Plan:{" "}
                <a href={planUrl} target="_blank" rel="noopener noreferrer">
                  {fileNameFromEngField(row.permit_plan, "permit_plan", ".pdf")}
                </a>
              </div>
            ) : null}
            {row.application_submitted_date ? (
              <div className="eng-drawings-summary-line">Applied: {row.application_submitted_date}</div>
            ) : null}
            {row.permit_number ? <div className="eng-drawings-summary-line">Permit #: {row.permit_number}</div> : null}
            {row.expiration_date ? (
              <div className="eng-drawings-summary-line">Expires: {row.expiration_date}</div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function EngineeringTabPanel({ projectRow, projectId, apiUrl, getAuthHeaders, onUpdated, viewerEmail, forceEdit }) {
  const canEdit = canManageEngineeringDrawings(viewerEmail);
  const [editing, setEditing] = useState(Boolean(forceEdit && canEdit));
  const [drawings, setDrawings] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const materialsCatalog = useEngineeringMaterialsCatalog();

  const engFromRow = projectRow?.acf_groups_json?.[ENG_GROUP] ?? projectRow?.acf_groups_json?.engineering_test_2 ?? {};
  const invFromRow = projectRow?.acf_groups_json?.[INV_GROUP] ?? projectRow?.acf_groups_json?.inventory_test_2 ?? {};

  const syncFromProject = useCallback(() => {
    setDrawings(normalizeConstructionDrawings(engFromRow?.permit_files));
    const mats = normalizeMaterialRows(invFromRow?.material_totals);
    setMaterials(mats.length ? mats : []);
  }, [engFromRow, invFromRow]);

  useEffect(() => {
    syncFromProject();
  }, [syncFromProject]);

  useEffect(() => {
    if (forceEdit && canEdit) setEditing(true);
  }, [forceEdit, canEdit]);

  const persistAcf = async (nextDrawings, nextMaterials) => {
    const ag = cloneAcfGroupsForPatch(projectRow);
    ag[ENG_GROUP] = {
      ...(ag[ENG_GROUP] && typeof ag[ENG_GROUP] === "object" ? ag[ENG_GROUP] : engFromRow),
      permit_files: nextDrawings,
    };
    ag[INV_GROUP] = {
      ...(ag[INV_GROUP] && typeof ag[INV_GROUP] === "object" ? ag[INV_GROUP] : invFromRow),
      material_totals: nextMaterials.filter((r) => r.item || r.slug || r.quantity),
    };
    const base = apiBase(apiUrl);
    await axios.patch(`${base}/projects/${projectId}`, { acf_groups_json: ag }, { headers: getAuthHeaders() });
  };

  const onSave = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const cleaned = drawings.map((row) => ({
        ...emptyConstructionDrawingRow(),
        ...row,
        permit_agency: String(row.permit_agency || "").trim(),
      }));
      const mats = materials.map((r) => {
        const slug = String(r.slug || "").trim();
        const item = String(r.item || "").trim();
        const hit = findCatalogMaterial(materialsCatalog, slug || item);
        return {
          slug: hit?.slug || slug,
          item: hit?.name || item,
          quantity: String(r.quantity || "").trim(),
        };
      });
      await persistAcf(cleaned, mats);
      onUpdated?.();
      setEditing(false);
    } catch (err) {
      setSaveError(err?.response?.data?.error || err?.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const addDrawing = async () => {
    const next = [...drawings, emptyConstructionDrawingRow()];
    setDrawings(next);
    if (!canEdit) return;
    try {
      await persistAcf(next, materials);
      onUpdated?.();
    } catch (err) {
      setSaveError(err?.response?.data?.error || err?.message || "Could not add drawing.");
    }
  };

  const updateDrawing = (index, key, value) => {
    setDrawings((prev) => prev.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
  };

  const removeDrawing = async (index) => {
    if (!window.confirm("Remove this construction drawing?")) return;
    const next = drawings.filter((_, i) => i !== index);
    setDrawings(next);
    if (!canEdit) return;
    try {
      await persistAcf(next, materials);
      onUpdated?.();
    } catch (err) {
      setSaveError(err?.response?.data?.error || err?.message || "Could not remove drawing.");
    }
  };

  const updateMaterial = (index, key, value) => {
    setMaterials((prev) => prev.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
  };

  const patchMaterial = (index, patch) => {
    setMaterials((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addMaterialRow = () => setMaterials((prev) => [...prev, emptyMaterialRow()]);

  const removeMaterialRow = (index) => {
    setMaterials((prev) => prev.filter((_, i) => i !== index));
  };

  const displayDrawings = normalizeConstructionDrawings(engFromRow?.permit_files);

  return (
    <div className="eng-tab-panel">
      <div className="eng-tab-toolbar">
        {canEdit ? (
          editing ? (
            <>
              <button type="button" className="project-dashboard-edit-toggle" disabled={saving} onClick={onSave}>
                {saving ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                className="project-field-file-btn"
                disabled={saving}
                onClick={() => {
                  syncFromProject();
                  setEditing(false);
                }}
              >
                Cancel
              </button>
            </>
          ) : (
            <button type="button" className="project-dashboard-edit-toggle" onClick={() => setEditing(true)}>
              Edit
            </button>
          )
        ) : (
          <p className="project-detail-muted eng-tab-readonly-hint">
            Construction drawings and materials can be edited by Coordinators, Drafters, Engineering Supervisors,
            Engineering Managers, and Administrators.
          </p>
        )}
      </div>
      {saveError ? (
        <p className="project-detail-error" role="alert">
          {saveError}
        </p>
      ) : null}

      <section className="eng-tab-section">
        <h3 className="eng-tab-section-title">Construction drawings (permit plans)</h3>
        <p className="project-detail-muted eng-tab-section-desc">
          Each drawing is followed by a permit application and issued permit. Record applied date, permit number, and
          expiration as they become available.
        </p>
        {editing && canEdit ? (
          <div className="eng-drawings-editor">
            {drawings.map((row, index) => (
              <div key={index} className="eng-drawing-card">
                <div className="eng-drawing-card-head">
                  <strong>Drawing {index + 1}</strong>
                  <button type="button" className="project-field-file-btn danger" onClick={() => removeDrawing(index)}>
                    Remove
                  </button>
                </div>
                <label className="eng-field">
                  <span>Agency / description</span>
                  <input
                    type="text"
                    value={row.permit_agency}
                    onChange={(e) => updateDrawing(index, "permit_agency", e.target.value)}
                  />
                </label>
                <EngineeringDrawingFileControl
                  projectId={projectId}
                  fieldPath={[ENG_GROUP, "permit_files", index, "permit_plan"]}
                  value={row.permit_plan}
                  getAuthHeaders={getAuthHeaders}
                  onUpdated={onUpdated}
                  label="Permit plan (construction drawing)"
                />
                <EngineeringDrawingFileControl
                  projectId={projectId}
                  fieldPath={[ENG_GROUP, "permit_files", index, "permit_application"]}
                  value={row.permit_application}
                  getAuthHeaders={getAuthHeaders}
                  onUpdated={onUpdated}
                  label="Permit application"
                />
                <div className="eng-drawing-fields-row">
                  <label className="eng-field">
                    <span>Applied date</span>
                    <input
                      type="text"
                      placeholder="MM/DD/YYYY"
                      value={row.application_submitted_date}
                      onChange={(e) => updateDrawing(index, "application_submitted_date", e.target.value)}
                    />
                  </label>
                  <label className="eng-field">
                    <span>Permit #</span>
                    <input
                      type="text"
                      value={row.permit_number}
                      onChange={(e) => updateDrawing(index, "permit_number", e.target.value)}
                    />
                  </label>
                  <label className="eng-field">
                    <span>Permit expiration</span>
                    <input
                      type="text"
                      placeholder="MM/DD/YYYY"
                      value={row.expiration_date}
                      onChange={(e) => updateDrawing(index, "expiration_date", e.target.value)}
                    />
                  </label>
                </div>
                <label className="eng-field eng-field--checkbox">
                  <input
                    type="checkbox"
                    checked={row.permit === true}
                    onChange={(e) => updateDrawing(index, "permit", e.target.checked)}
                  />
                  <span>Permit issued</span>
                </label>
              </div>
            ))}
            <button type="button" className="project-field-file-btn" onClick={addDrawing}>
              + Add construction drawing
            </button>
          </div>
        ) : (
          <EngineeringDrawingsSummary drawings={displayDrawings} />
        )}
      </section>

      <section className="eng-tab-section">
        <h3 className="eng-tab-section-title">Materials</h3>
        {editing && canEdit ? (
          <>
            <EngineeringMaterialsCatalogEditor viewerEmail={viewerEmail} />
            <div className="eng-materials-editor">
              <table className="eng-materials-grid">
                <thead>
                  <tr>
                    <th scope="col">Material</th>
                    <th scope="col">Amount</th>
                    <th scope="col" className="eng-materials-grid-actions" />
                  </tr>
                </thead>
                <tbody>
                  {(materials.length ? materials : [emptyMaterialRow()]).map((row, index) => (
                    <tr key={index}>
                      <td>
                        <MaterialCatalogSelect
                          value={row.slug || row.item}
                          catalog={materialsCatalog}
                          onChange={(slug, hit) => {
                            patchMaterial(index, {
                              slug: hit?.slug || slug,
                              item: hit?.name || slug,
                            });
                          }}
                        />
                      </td>
                      <td>
                        <label className="eng-materials-qty-field">
                          <span className="eng-materials-qty-unit">
                            {unitLabelForMaterialName(row.slug || row.item)}
                          </span>
                          <input
                            type="text"
                            inputMode="decimal"
                            placeholder="0"
                            value={row.quantity}
                            onChange={(e) => updateMaterial(index, "quantity", e.target.value)}
                          />
                        </label>
                      </td>
                      <td className="eng-materials-grid-actions">
                        <button
                          type="button"
                          className="project-field-file-btn danger"
                          onClick={() => removeMaterialRow(index)}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button type="button" className="project-field-file-btn" onClick={addMaterialRow}>
                + Add row
              </button>
            </div>
          </>
        ) : (
          <EngineeringMaterialsSummary rows={invFromRow?.material_totals} />
        )}
      </section>
    </div>
  );
}

const PROJECT_LANDING_STYLES = `
.project-detail-shell.project-detail-shell--landing {
  max-width: none;
  width: auto;
  margin: -12px -16px;
  padding: 20px 24px 64px;
  min-height: calc(100vh - 24px);
  min-height: calc(100dvh - 24px);
  box-sizing: border-box;
  background: #f8fafc;
}
.project-detail-landing-inner {
  max-width: 1320px;
  margin: 0 auto;
  width: 100%;
}
.project-landing-back { margin-bottom: 12px; display: inline-block; }
.project-landing-parent-link { margin: 0 0 12px; font-size: 13px; }
.project-landing-hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px 32px;
  margin: 0 0 18px;
  padding: 0 0 18px;
  border-bottom: 1px solid #e5e7eb;
}
.project-landing-hero-main { flex: 1; min-width: 0; }
.project-landing-brand-row { display: flex; align-items: flex-start; gap: 16px; }
.project-landing-logo {
  width: 52px;
  height: 52px;
  border-radius: 999px;
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);
}
.project-landing-logo .project-landing-icon { width: 26px; height: 26px; }
.project-landing-brand-copy { min-width: 0; flex: 1; }
.project-landing-title-row { display: flex; flex-wrap: wrap; align-items: center; gap: 10px 12px; }
.project-landing-title {
  margin: 0;
  font-size: clamp(1.35rem, 2.2vw, 1.75rem);
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.02em;
  line-height: 1.25;
}
.project-landing-status-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
}
.project-landing-status-pill--complete {
  color: #166534;
  background: #dcfce7;
  border-color: #bbf7d0;
}
.project-landing-location {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 8px 0 0;
  font-size: 14px;
  color: #64748b;
}
.project-landing-location--az { align-items: flex-start; }
.project-landing-location-az {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.project-landing-location-az-label {
  display: inline-block;
  min-width: 1.1em;
  margin-right: 6px;
  font-weight: 700;
  color: #475569;
}
.project-landing-location-icon { width: 16px; height: 16px; color: #94a3b8; flex-shrink: 0; }
.project-landing-tags { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.project-landing-tag {
  display: inline-block;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  color: #475569;
  background: #fff;
  border: 1px solid #e2e8f0;
}
.project-landing-tag--phase {
  font-weight: 700;
}
.project-landing-tag--phase-default {
  background: #f1f5f9;
  border-color: #cbd5e1;
  color: #334155;
}
.project-landing-hero-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}
.project-landing-phase-complete-btn {
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 18px;
  border-radius: 10px;
  border: 1px solid #15803d;
  background: #22c55e;
  color: #fff;
  cursor: pointer;
  white-space: nowrap;
}
.project-landing-phase-complete-btn:hover { background: #16a34a; }
.project-landing-phase-complete-btn:disabled { opacity: 0.7; cursor: not-allowed; }
.phase-complete-email-modal {
  max-width: 36rem;
  max-height: min(90vh, 640px);
}
.phase-complete-email-modal-note {
  margin: 0 0 12px;
  font-size: 13px;
}
.project-landing-hero-aside {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  flex-shrink: 0;
}
.project-landing-edit-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 18px;
  border-radius: 10px;
  border: none;
  background: #2563eb;
  color: #fff;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(37, 99, 235, 0.2);
}
.project-landing-edit-btn:hover { background: #1d4ed8; }
.project-landing-edit-btn:disabled { opacity: 0.7; cursor: not-allowed; }
.project-landing-edit-btn-icon { width: 16px; height: 16px; }
.project-landing-delete-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 18px;
  border-radius: 10px;
  border: 1px solid #fecaca;
  background: #fff;
  color: #b91c1c;
  cursor: pointer;
}
.project-landing-delete-btn:hover { background: #fef2f2; }
.project-landing-delete-btn:disabled { opacity: 0.7; cursor: not-allowed; }
.project-landing-invoiced {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 13px;
  color: #64748b;
}
.project-landing-invoiced-icon { width: 16px; height: 16px; color: #94a3b8; }
.project-landing-tabs { margin-bottom: 20px; background: transparent; }
.project-landing-icon { display: block; flex-shrink: 0; }
.project-detail-tab-content {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}
.commercial-pending-edits-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin: 0 0 16px;
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  color: #991b1b;
  font-size: 14px;
}
.commercial-pending-edits-banner strong {
  font-weight: 600;
  color: #7f1d1d;
}
.project-detail-tabs { display: flex; flex-wrap: wrap; gap: 4px; margin: 0 0 20px; border-bottom: 1px solid #e5e7eb; }
.project-detail-tab { font: inherit; font-size: 14px; font-weight: 500; padding: 12px 18px; margin: 0 0 -1px; border: none; border-bottom: 2px solid transparent; background: transparent; color: #64748b; cursor: pointer; }
.project-detail-tab:hover { color: #334155; background: #f8fafc; }
.project-detail-tab--active { color: #2563eb; border-bottom-color: #2563eb; font-weight: 600; }
.project-summary-dashboard { display: flex; flex-direction: column; gap: 16px; }
.project-summary-kpi-row { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; }
@media (max-width: 1100px) { .project-summary-kpi-row { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 640px) { .project-summary-kpi-row { grid-template-columns: 1fr; } }
.project-kpi-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 16px 18px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 128px;
}
.project-kpi-card-head { display: flex; align-items: center; gap: 8px; margin-bottom: 2px; }
.project-kpi-card-head .project-landing-notes-save { margin-left: auto; }
.project-kpi-card-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.project-kpi-card-icon--blue { background: #eff6ff; color: #2563eb; }
.project-kpi-card-icon--green { background: #ecfdf5; color: #16a34a; }
.project-kpi-card-icon--amber { background: #fffbeb; color: #d97706; }
.project-kpi-card-icon .project-landing-icon { width: 18px; height: 18px; }
.project-kpi-card-title { margin: 0; font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .06em; }
.project-kpi-big { font-size: 1.5rem; font-weight: 700; color: #0f172a; line-height: 1.1; }
.project-kpi-contact-name {
  font-size: 1.15rem;
  word-break: break-word;
}
.project-kpi-contact-link {
  font-size: 13px;
  color: #2563eb;
  text-decoration: none;
  word-break: break-word;
}
.project-kpi-contact-link:hover { color: #1d4ed8; text-decoration: underline; }
.project-landing-notes-input {
  width: 100%;
  box-sizing: border-box;
  font: inherit;
  font-size: 14px;
  line-height: 1.45;
  color: #0f172a;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 12px;
  resize: vertical;
  min-height: 96px;
  background: #f8fafc;
}
.project-landing-notes-input:focus {
  outline: none;
  border-color: #93c5fd;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}
.project-landing-notes-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #334155;
  white-space: pre-wrap;
  word-break: break-word;
}
.project-landing-notes-save {
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: #2563eb;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 5px 10px;
  cursor: pointer;
}
.project-landing-notes-save:hover:not(:disabled) { background: #dbeafe; }
.project-landing-notes-save:disabled { opacity: 0.6; cursor: default; }
.project-kpi-sub { font-size: 13px; color: #94a3b8; }
.project-kpi-note { font-size: 12px; color: #64748b; margin-top: 2px; }
.project-kpi-bar { height: 6px; background: #e5e7eb; border-radius: 999px; overflow: hidden; margin-top: 6px; }
.project-kpi-bar-fill { height: 100%; border-radius: 999px; }
.project-kpi-bar-fill--blue { background: #2563eb; }
.project-kpi-bar-fill--green { background: #22c55e; }
.project-kpi-progress { display: flex; align-items: center; gap: 14px; margin-top: 4px; }
.project-kpi-progress-meta { display: flex; flex-direction: column; gap: 2px; }
.project-kpi-ring { position: relative; flex-shrink: 0; }
.project-kpi-ring-label { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 700; }
.project-kpi-card--wo-approval { overflow: visible; }
.landing-wo-approval-list { display: flex; flex-direction: column; gap: 8px; margin-top: 8px; }
.landing-wo-approval-row { display: flex; flex-direction: column; gap: 2px; }
.landing-wo-approval-label { font-size: 11px; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: .04em; }
.landing-wo-approval-link {
  font: inherit; font-size: 13px; font-weight: 500; color: #2563eb; background: none; border: none;
  padding: 0; cursor: pointer; text-align: left; text-decoration: underline; word-break: break-word;
}
.landing-wo-approval-link:hover { color: #1d4ed8; }
.landing-wo-approval-missing { font-size: 13px; color: #94a3b8; }
.project-summary-mid-row { display: grid; grid-template-columns: 1.05fr 0.85fr 1.35fr; gap: 14px; }
.project-summary-mid-row--wide-map { grid-template-columns: 1.05fr 2.2fr; }
.project-summary-bottom-row { display: grid; grid-template-columns: 1.15fr 0.95fr; gap: 14px; }
.project-summary-bottom-row--with-notes {
  grid-template-columns: 1.1fr 0.95fr 0.85fr;
}
.project-summary-panel-notes {
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.project-summary-panel-notes .project-landing-notes-input,
.project-summary-panel-notes .project-landing-notes-text {
  flex: 1;
}
@media (max-width: 1024px) {
  .project-landing-hero { flex-direction: column; }
  .project-landing-hero-aside { align-items: flex-start; flex-direction: row; flex-wrap: wrap; }
  .project-summary-mid-row, .project-summary-bottom-row { grid-template-columns: 1fr; }
}
.project-summary-panel {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 18px 20px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
}
.project-summary-panel-title {
  margin: 0 0 14px;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: .06em;
  display: flex;
  align-items: center;
  gap: 8px;
}
.project-summary-panel-title-icon { width: 16px; height: 16px; color: #2563eb; }
.project-summary-panel-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 14px;
}
.project-summary-panel-title-row .project-summary-panel-title { margin: 0; }
.project-landing-detail-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 14px; }
.project-landing-detail-item { display: flex; align-items: flex-start; gap: 12px; }
.project-landing-detail-icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #eff6ff;
  color: #2563eb;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.project-landing-detail-icon .project-landing-icon { width: 17px; height: 17px; }
.project-landing-detail-copy { min-width: 0; }
.project-landing-detail-label { display: block; font-size: 12px; color: #94a3b8; margin-bottom: 2px; }
.project-landing-detail-value { display: block; font-size: 14px; font-weight: 500; color: #0f172a; line-height: 1.4; word-break: break-word; }
.landing-detail-file-link {
  display: inline;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-weight: 500;
  color: #2563eb;
  text-align: left;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.landing-detail-file-link:hover { color: #1d4ed8; }
.project-kpi-usa-tickets {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.project-kpi-usa-tickets-label { font-weight: 600; color: #475569; }
.project-landing-usa-ticket-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.project-landing-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 10px;
  min-height: 140px;
  padding: 12px;
}
.project-landing-empty-icon {
  width: 44px;
  height: 44px;
  border-radius: 999px;
  background: #eff6ff;
  color: #2563eb;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.project-landing-empty-icon .project-landing-icon { width: 22px; height: 22px; }
.project-map-preview--compact .project-map-esri-container { height: 240px; border-radius: 10px; overflow: hidden; }
.project-summary-permit-list, .project-summary-activity-list, .project-summary-schedule { list-style: none; margin: 0; padding: 0; }
.project-summary-permit-list { display: flex; flex-direction: column; gap: 10px; }
.project-summary-permit-head { display: flex; justify-content: space-between; gap: 8px; }
.project-summary-permit-badge { font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 4px; text-transform: uppercase; }
.project-summary-permit-badge--ok { background: #dcfce7; color: #166534; }
.project-summary-permit-badge--pending { background: #ffedd5; color: #9a3412; }
.project-summary-permit-date { display: block; font-size: 12px; color: #9ca3af; margin-top: 4px; }
.project-summary-schedule { display: flex; flex-direction: column; gap: 10px; }
.project-summary-schedule-row {
  display: grid;
  grid-template-columns: 92px 1fr auto 22px;
  gap: 10px;
  align-items: center;
  font-size: 13px;
}
.project-summary-schedule-track { height: 8px; background: #f1f5f9; border-radius: 999px; overflow: hidden; cursor: help; }
.project-summary-schedule-fill { height: 100%; width: 0; background: #d1d5db; border-radius: 999px; }
.project-summary-schedule-row--done .project-summary-schedule-fill { width: 100%; background: #86efac; }
.project-summary-schedule-row--active .project-summary-schedule-fill { width: 58%; background: #60a5fa; }
.project-summary-schedule-row--na .project-summary-schedule-fill { width: 100%; background: #e2e8f0; }
.project-summary-schedule-row--na .project-summary-schedule-phase,
.project-summary-schedule-row--na .project-summary-schedule-sub { color: #94a3b8; }
.project-summary-schedule-row--na .project-summary-schedule-track { opacity: 0.85; }
.project-summary-schedule-phase { font-weight: 500; color: #334155; }
.project-summary-schedule-sub { font-size: 12px; color: #64748b; text-align: right; max-width: 120px; }
.project-summary-schedule-icon { display: flex; align-items: center; justify-content: center; }
.project-summary-schedule-icon-svg { width: 18px; height: 18px; }
.project-summary-schedule-row--done .project-summary-schedule-icon-svg { color: #22c55e; }
.project-summary-schedule-row--active .project-summary-schedule-icon-svg { color: #2563eb; }
.project-summary-activity-list { display: flex; flex-direction: column; gap: 12px; }
.project-summary-activity-item { display: flex; align-items: flex-start; gap: 10px; }
.project-landing-activity-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  margin-top: 5px;
  flex-shrink: 0;
}
.project-landing-activity-dot--blue { background: #3b82f6; }
.project-landing-activity-dot--green { background: #22c55e; }
.project-landing-activity-dot--amber { background: #f59e0b; }
.project-summary-activity-body { min-width: 0; }
.project-summary-activity-field { display: block; font-size: 14px; font-weight: 500; color: #0f172a; }
.project-summary-activity-meta { font-size: 12px; color: #94a3b8; }
.project-landing-activity-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  color: #64748b;
  cursor: pointer;
  padding: 0;
}
.project-landing-activity-link .project-landing-icon { width: 16px; height: 16px; }
.project-detail-tab-panel { margin-bottom: 0; width: 100%; }
.project-detail-shell.project-detail-shell--landing {
  --pd-phase-fill: #f8fafc;
  --pd-phase-bar: #e5e7eb;
  background: #f8fafc;
}
.project-detail-shell--landing .project-detail-section-card,
.project-detail-shell--landing .project-detail-aux-card,
.project-detail-shell--landing .commercial-section-panel {
  background: #fff !important;
  border: 1px solid #e5e7eb !important;
  border-left: 1px solid #e5e7eb !important;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
}
.project-landing-footer-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
}
.project-detail-shell--landing .project-landing-footer-card {
  margin-bottom: 0;
}
.project-detail-shell--landing .project-changelog-section.project-landing-footer-card {
  padding: 0;
  overflow: hidden;
}
.project-detail-shell--landing .project-comments-section.project-landing-footer-card {
  padding: 18px 20px;
}
.project-detail-shell--landing .project-changelog-section {
  margin-top: 0;
}
.project-detail-shell--landing .project-comments-section {
  margin-top: 0;
}
.project-detail-shell--landing .project-changelog-ribbon {
  background: #fff;
  border: none;
  border-radius: 0;
  box-shadow: none;
  width: 100%;
}
.project-detail-shell--landing .project-changelog-ribbon:hover {
  background: #f8fafc;
  border-color: transparent;
}
.project-detail-shell--landing .project-changelog-ribbon-accent {
  display: none;
}
.project-detail-shell--landing .project-changelog-ribbon-main {
  padding: 18px 20px 16px;
}
.project-detail-shell--landing .project-changelog-ribbon-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
}
.project-detail-shell--landing .project-changelog-ribbon-meta {
  font-size: 13px;
  color: #94a3b8;
  font-weight: 400;
}
.project-detail-shell--landing .project-changelog-ribbon-chevron {
  padding-right: 20px;
  color: #94a3b8;
  font-size: 10px;
}
.project-detail-shell--landing .project-changelog-panel {
  margin-top: 0;
  padding: 0 20px 18px;
  border-top: 1px solid #f1f5f9;
}
.project-detail-shell--landing .project-changelog-subheading {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #64748b;
}
.project-detail-shell--landing .project-wp-changelog-card {
  background: #f8fafc;
  border-color: #e5e7eb;
  border-radius: 10px;
}
.project-detail-shell--landing .project-change-log-wrap {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}
.project-detail-shell--landing .project-change-log-table th {
  background: #f8fafc;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.project-detail-shell--landing .project-comments-section .project-detail-section-heading {
  margin: 0 0 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
}
.project-detail-shell--landing .project-comments-intro {
  margin: 0 0 16px;
  font-size: 13px;
  line-height: 1.45;
}
.project-detail-shell--landing .project-comments-thread {
  margin-bottom: 16px;
}
.project-detail-shell--landing .project-comments-card {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
}
.project-detail-shell--landing .project-comments-composer {
  border-top: 1px solid #f1f5f9;
  padding-top: 16px;
  margin-top: 0;
}
.project-detail-shell--landing .project-comments-textarea {
  max-width: none;
  background: #fff;
  border-radius: 10px;
  border-color: #e5e7eb;
  min-height: 96px;
}
.project-detail-shell--landing .project-comments-textarea:focus {
  outline: 2px solid #93c5fd;
  outline-offset: 1px;
  border-color: #93c5fd;
}
.project-detail-shell--landing .project-comments-submit-btn {
  margin-top: 0;
  padding: 10px 18px;
  font: inherit;
  font-weight: 600;
  font-size: 14px;
  color: #fff;
  background: #2563eb;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(37, 99, 235, 0.2);
}
.project-detail-shell--landing .project-comments-submit-btn:hover:not(:disabled) {
  background: #1d4ed8;
}
.project-detail-shell--landing .project-comments-submit-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
.commercial-section-panel { padding: 18px 20px; margin-bottom: 0; }
.commercial-section-title { margin: 0 0 16px; font-size: 15px; font-weight: 600; color: #111827; }
.commercial-fields { display: flex; flex-direction: column; gap: 4px; }
.commercial-field-row { display: grid; grid-template-columns: minmax(140px, 200px) 1fr; gap: 8px 16px; align-items: start; padding: 8px 0; border-bottom: 1px solid #f1f5f9; }
.commercial-field-row:last-child { border-bottom: none; }
.commercial-field-label { font-size: 13px; font-weight: 500; color: #64748b; }
.commercial-field-value { font-size: 14px; color: #111827; }
.commercial-field-input, .commercial-field-textarea { font: inherit; font-size: 14px; padding: 8px 10px; border: 1px solid #d1d5db; border-radius: 8px; background: #fff; color: #111827; max-width: 100%; box-sizing: border-box; }
.commercial-field-textarea { width: 100%; max-width: 520px; resize: vertical; }
.commercial-multiselect { display: flex; flex-wrap: wrap; gap: 10px 14px; }
.commercial-multiselect-option { display: flex; align-items: center; gap: 6px; font-size: 13px; color: #374151; }
.commercial-bool-label { display: flex; align-items: center; gap: 8px; font-size: 14px; }
.commercial-repeater { margin: 12px 0 16px; }
.commercial-repeater-title { margin: 0 0 10px; font-size: 14px; font-weight: 600; color: #374151; }
.commercial-repeater-card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px 14px; margin-bottom: 10px; background: #f8fafc; }
.commercial-repeater-card-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.commercial-tab-read-panel { background: #fff; border: 1px solid #e5e7eb; border-radius: 14px; padding: 16px 18px; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04); width: 100%; box-sizing: border-box; }
.commercial-documents-panel { margin-top: 0; }
.commercial-read-fields { display: flex; flex-direction: column; gap: 2px; }
.commercial-read-row { display: grid; grid-template-columns: minmax(140px, 200px) 1fr; gap: 8px 16px; align-items: start; padding: 8px 0; border-bottom: 1px solid #f1f5f9; }
.commercial-read-row:last-child { border-bottom: none; }
.commercial-read-label { font-size: 13px; font-weight: 500; color: #64748b; }
.commercial-read-value { font-size: 14px; color: #111827; }
.commercial-read-repeater { margin: 12px 0 0; }
.commercial-read-repeater-title { margin: 0 0 8px; font-size: 14px; font-weight: 600; color: #374151; }
.commercial-read-repeater-card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px 14px; margin-bottom: 10px; background: #f8fafc; }
.commercial-read-repeater-head { font-size: 13px; font-weight: 600; color: #475569; margin-bottom: 8px; }
.commercial-file-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.commercial-file-list-item { margin: 0; }
.commercial-file-display { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.commercial-file-display-label { font-size: 13px; color: #64748b; }
.commercial-file-view-link { font: inherit; font-size: 13px; font-weight: 500; color: #2563eb; background: none; border: none; padding: 0; cursor: pointer; text-decoration: underline; }
.commercial-file-view-link:hover { color: #1d4ed8; }
.commercial-file-view-link:disabled { opacity: 0.6; cursor: not-allowed; }
.commercial-file-attached { margin: 0 0 8px; font-size: 13px; }
.commercial-materials-table { width: 100%; max-width: 480px; border-collapse: collapse; font-size: 13px; margin-top: 4px; }
.commercial-materials-table th, .commercial-materials-table td { border: 1px solid #e5e7eb; padding: 6px 10px; text-align: left; }
.commercial-materials-table th { background: #f8fafc; font-weight: 600; color: #475569; }
.commercial-section-panel { width: 100%; box-sizing: border-box; }
.eng-tab-panel { display: flex; flex-direction: column; gap: 20px; }
.eng-tab-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.eng-tab-readonly-hint { margin: 0; font-size: 13px; }
.eng-tab-section { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 16px 18px; }
.eng-tab-section-title { margin: 0 0 8px; font-size: 15px; font-weight: 600; color: #111827; }
.eng-tab-section-desc { margin: 0 0 14px; font-size: 13px; }
.eng-drawings-editor { display: flex; flex-direction: column; gap: 14px; }
.eng-drawing-card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 14px; background: #fafafa; display: flex; flex-direction: column; gap: 10px; }
.eng-drawing-card-head { display: flex; justify-content: space-between; align-items: center; }
.eng-field { display: flex; flex-direction: column; gap: 4px; font-size: 13px; }
.eng-field input[type="text"] { font: inherit; padding: 8px 10px; border: 1px solid #d1d5db; border-radius: 6px; }
.eng-field--checkbox { flex-direction: row; align-items: center; gap: 8px; }
.eng-drawing-fields-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 10px; }
.eng-drawing-file { display: flex; flex-direction: column; gap: 6px; font-size: 13px; }
.eng-drawing-file-label { font-weight: 500; color: #374151; }
.eng-drawing-file-link { font-size: 13px; word-break: break-all; }
.eng-drawings-summary-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }
.eng-drawings-summary-card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px 14px; background: #fafafa; }
.eng-drawings-summary-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 6px; }
.eng-drawings-summary-line { font-size: 13px; color: #4b5563; margin-top: 4px; }
.eng-materials-editor { overflow-x: auto; }
.eng-materials-grid { width: 100%; border-collapse: collapse; font-size: 13px; }
.eng-materials-grid th, .eng-materials-grid td { border: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; }
.eng-materials-grid th { background: #f3f4f6; font-weight: 600; }
.eng-materials-grid input, .eng-materials-grid select { width: 100%; font: inherit; padding: 6px 8px; border: 1px solid #d1d5db; border-radius: 4px; box-sizing: border-box; }
.eng-materials-grid-actions { width: 40px; text-align: center; }
.eng-materials-select { min-width: 220px; }
.eng-materials-qty-field { display: flex; flex-direction: column; gap: 4px; }
.eng-materials-qty-unit { font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.02em; }
.eng-materials-unit-hint { font-size: 11px; color: #9ca3af; font-weight: 500; }
.eng-materials-catalog { margin-bottom: 12px; }
.eng-materials-catalog-panel { margin-top: 10px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px; background: #f9fafb; }
.eng-materials-catalog-list { list-style: none; margin: 0 0 12px; padding: 0; max-height: 320px; overflow-y: auto; }
.eng-materials-catalog-list li { display: flex; align-items: center; gap: 8px; padding: 6px 0; border-bottom: 1px solid #e5e7eb; font-size: 13px; }
.eng-materials-catalog-name { flex: 1.4; min-width: 140px; padding: 6px 8px; border: 1px solid #d1d5db; border-radius: 4px; font: inherit; }
.eng-materials-catalog-slug { flex: 1; min-width: 110px; padding: 6px 8px; border: 1px solid #d1d5db; border-radius: 4px; font: inherit; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; font-size: 12px; color: #4b5563; }
.eng-materials-catalog-unit { font-size: 12px; color: #374151; min-width: 110px; padding: 6px 8px; border: 1px solid #d1d5db; border-radius: 4px; font: inherit; background: #fff; }
.eng-materials-catalog-add { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-bottom: 10px; }
.eng-materials-catalog-add input { flex: 1; min-width: 180px; padding: 6px 8px; border: 1px solid #d1d5db; border-radius: 4px; font: inherit; }
.eng-materials-catalog-add select { padding: 6px 8px; border: 1px solid #d1d5db; border-radius: 4px; font: inherit; }
.eng-materials-catalog-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.eng-materials-summary-meta { margin: 0 0 10px; font-size: 13px; color: #6b7280; }
.eng-materials-summary-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.eng-materials-summary-table th, .eng-materials-summary-table td { border: 1px solid #e5e7eb; padding: 6px 10px; text-align: left; }
.eng-materials-summary-table th { background: #f9fafb; font-weight: 600; }
`;

function looksWpChangeEntry(obj) {
  return (
    obj != null &&
    typeof obj === "object" &&
    !Array.isArray(obj) &&
    (Object.prototype.hasOwnProperty.call(obj, "field") ||
      Object.prototype.hasOwnProperty.call(obj, "date"))
  );
}

/** Match backend acfChangeLogImport.parseValueLines for standard ACF lines. */
function parseWpChangeFirstLine(value) {
  const lines = Array.isArray(value)
    ? value.map((x) => String(x ?? ""))
    : [String(value ?? "")];
  const first = lines[0] || "";
  const changed = String(first).match(/^Changed from '([\s\S]*)' to '([\s\S]*)'$/);
  if (changed) {
    return { before: changed[1], after: changed[2], detail: lines.join("\n").trim() };
  }
  return { before: null, after: null, detail: lines.join("\n").trim() || first };
}

/**
 * WordPress-style `change_log` rows from `acf_groups_json["Change Log"].change_log`
 * and from ACF domain payloads that expose the same shape.
 */
function gatherEmbeddedChangeLogItems(projectRow, acfDomains) {
  const items = [];
  const ag = projectRow?.acf_groups_json;
  const arr = ag?.["Change Log"]?.change_log;
  if (Array.isArray(arr)) {
    arr.forEach((entry, i) => {
      if (!looksWpChangeEntry(entry)) return;
      items.push({ entry, key: `g-${i}` });
    });
  }
  if (acfDomains && typeof acfDomains === "object") {
    for (const [domainKey, payload] of Object.entries(acfDomains)) {
      if (!payload || typeof payload !== "object") continue;
      let list = null;
      if (Array.isArray(payload.change_log)) list = payload.change_log;
      else if (Array.isArray(payload) && payload.length > 0 && looksWpChangeEntry(payload[0])) {
        list = payload;
      }
      if (!Array.isArray(list)) continue;
      list.forEach((entry, i) => {
        if (!looksWpChangeEntry(entry)) return;
        items.push({ entry, key: `d-${domainKey}-${i}`, domain: domainKey });
      });
    }
  }
  return items;
}

/** Same pattern as backend acfChangeLogImport.parseWpChangeLogDate → milliseconds for sorting. */
function wpChangeDateToMs(dateStr) {
  const m = String(dateStr || "")
    .trim()
    .match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})$/);
  if (!m) return NaN;
  const mo = Number(m[1]);
  const d = Number(m[2]);
  const y = Number(m[3]);
  const hh = Number(m[4]);
  const mm = Number(m[5]);
  const ss = Number(m[6]);
  if (![mo, d, y, hh, mm, ss].every(Number.isFinite)) return NaN;
  const dt = new Date(y, mo - 1, d, hh, mm, ss);
  const t = dt.getTime();
  return Number.isNaN(t) ? NaN : t;
}

function sortEmbeddedByDateDesc(items) {
  return [...items].sort((a, b) => {
    const ma = wpChangeDateToMs(a.entry.date);
    const mb = wpChangeDateToMs(b.entry.date);
    const va = Number.isNaN(ma) ? Number.NEGATIVE_INFINITY : ma;
    const vb = Number.isNaN(mb) ? Number.NEGATIVE_INFINITY : mb;
    return vb - va;
  });
}

function sortAuditByDateDesc(rows) {
  return [...rows].sort((a, b) => {
    const ta = a.changed_at ? new Date(a.changed_at).getTime() : NaN;
    const tb = b.changed_at ? new Date(b.changed_at).getTime() : NaN;
    const va = Number.isNaN(ta) ? Number.NEGATIVE_INFINITY : ta;
    const vb = Number.isNaN(tb) ? Number.NEGATIVE_INFINITY : tb;
    return vb - va;
  });
}

function apiBase(url) {
  return String(url || "").replace(/\/$/, "");
}

function arcgisSearchUrl(serviceLocation) {
  const q = String(serviceLocation || "").trim();
  if (!q) return "";
  return `https://www.arcgis.com/apps/mapviewer/index.html?find=${encodeURIComponent(q)}`;
}

function objectEntriesSorted(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return [];
  return Object.entries(obj).sort(([a], [b]) => a.localeCompare(b));
}

/** ACF `choices` or row `subtype` — same idea as legacy WP “choices”. */
function projectDetailChoicesLabel(row) {
  const s = summaryGroup(row);
  const c = s?.choices;
  if (Array.isArray(c) && c.length) {
    return c
      .map((x) => (x == null ? "" : String(x).trim()))
      .filter(Boolean)
      .join(", ");
  }
  if (c != null && String(c).trim() !== "") return String(c).trim();
  if (row?.subtype != null && String(row.subtype).trim() !== "") return String(row.subtype).trim();
  return "";
}

function projectDetailPhaseBadge(row) {
  const pl = row?.phase_label;
  if (pl != null && String(pl).trim() !== "") return String(pl).trim();
  return phaseLabelForActiveSubphase(row) || "";
}

function formatInvoicedHeaderLine(row) {
  const raw = row?.invoiced_date;
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `Invoiced ${iso[2]}/${iso[3]}/${iso[1]}`;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const y = d.getFullYear();
    return `Invoiced ${mo}/${day}/${y}`;
  }
  return `Invoiced ${s}`;
}

/** Strip marketplace metadata from PATCH /projects/:id responses before treating as a project row. */
function projectRowFromPatchResponse(data) {
  if (!data || typeof data !== "object" || data.id == null) return null;
  const { marketplacePhaseRewards, marketplaceReward, ...row } = data;
  return row;
}

export default function ProjectDetailPage({ apiUrl, getAuthHeaders }) {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMobileViewport();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [changeLog, setChangeLog] = useState([]);
  const [changelogOpen, setChangelogOpen] = useState(false);
  /** Full field editors (files + ACF) — admins toggle via "Edit"; others stay on dashboard overview until matrix-driven edit lists exist. */
  const [detailsEditMode, setDetailsEditMode] = useState(false);
  const [phaseAdvancing, setPhaseAdvancing] = useState(false);
  const [phaseAdvanceError, setPhaseAdvanceError] = useState("");
  const [phaseEmailNote, setPhaseEmailNote] = useState("");
  const [phaseMarketplaceNote, setPhaseMarketplaceNote] = useState("");
  const [phaseEmailModal, setPhaseEmailModal] = useState(null);
  const [phaseEmailPreview, setPhaseEmailPreview] = useState(null);
  const [phaseEmailPreviewLoading, setPhaseEmailPreviewLoading] = useState(false);
  const [phaseEmailPreviewError, setPhaseEmailPreviewError] = useState("");
  const [phaseEmailSending, setPhaseEmailSending] = useState(false);
  const [phaseEmailSendError, setPhaseEmailSendError] = useState("");
  const [activeTab, setActiveTab] = useState("summary");
  const [commercialDraft, setCommercialDraft] = useState(null);
  /** Snapshot at edit start — pending banner compares draft to this, not live server (files sync both sides). */
  const [commercialEditBaseline, setCommercialEditBaseline] = useState(null);
  const [commercialSaving, setCommercialSaving] = useState(false);
  const [commercialSaveError, setCommercialSaveError] = useState("");
  const [archivingProject, setArchivingProject] = useState(false);
  const [archiveError, setArchiveError] = useState("");
  const [expansionDraft, setExpansionDraft] = useState(null);
  const [expansionEditBaseline, setExpansionEditBaseline] = useState(null);
  const [contractDerivedConstruction, setContractDerivedConstruction] = useState(null);
  const [interconnectFields, setInterconnectFields] = useState(null);
  const [permitCoverage, setPermitCoverage] = useState(null);

  const loadProject = useCallback(
    async (silent) => {
      const base = apiBase(apiUrl);
      const headers = getAuthHeaders();

      if (!silent) {
        setLoading(true);
        setError("");
        setData(null);
        setChangeLog([]);
      }

      try {
        const res = await axios.get(`${base}/projects/${encodeURIComponent(id)}`, { headers });
        setData(res.data);
        const projectId = res.data?.id;
        const serverDomains = readCommercialDomainsForRow(res.data, res.data?.acf_domains);
        setCommercialDraft((prev) => {
          if (!prev) return prev;
          return mergeCommercialFileFieldsFromServer(prev, serverDomains);
        });
        setCommercialEditBaseline((prev) => {
          if (!prev) return prev;
          return mergeCommercialFileFieldsFromServer(prev, serverDomains);
        });
        if (isCvinExpansion(res.data)) {
          const expDomains = readExpansionEditDomains(res.data, res.data?.acf_domains);
          setExpansionDraft((prev) => {
            if (!prev) return prev;
            return mergeCommercialFileFieldsFromServer(prev, expDomains);
          });
          setExpansionEditBaseline((prev) => {
            if (!prev) return prev;
            return mergeCommercialFileFieldsFromServer(prev, expDomains);
          });
        }
        const chOn = isPluginEnabled(PLUGIN_IDS.CHANGE_HISTORY);
        if (chOn && projectId != null) {
          try {
            const logRes = await axios.get(`${base}/projects/${projectId}/change-log`, { headers });
            setChangeLog(logRes.data || []);
          } catch {
            setChangeLog([]);
          }
        } else {
          setChangeLog([]);
        }
        setError("");
      } catch (err) {
        const status = err.response?.status;
        const apiMsg = err.response?.data?.error;
        const isJsonError = typeof apiMsg === "string" && apiMsg.length > 0;

        if (!silent && status === 404) {
          try {
            const listRes = await axios.get(`${base}/projects`, { headers });
            const idKey = String(id).trim();
            const row = (listRes.data || []).find(
              (r) =>
                String(r.id) === String(id) ||
                (r.work_order != null && String(r.work_order).trim() === idKey)
            );
            if (row) {
              setData({ ...row, acf_domains: row.acf_domains || {} });
              const chOn = isPluginEnabled(PLUGIN_IDS.CHANGE_HISTORY);
              if (chOn) {
                try {
                  const logRes = await axios.get(`${base}/projects/${row.id}/change-log`, { headers });
                  setChangeLog(logRes.data || []);
                } catch {
                  setChangeLog([]);
                }
              } else {
                setChangeLog([]);
              }
              setError("");
              return;
            }
          } catch (listErr) {
            setError(listErr?.response?.data?.error || listErr.message || "Failed to load project list");
            return;
          }
          setError(isJsonError ? apiMsg : "Project not found");
          return;
        }

        if (!silent) {
          setError(isJsonError ? apiMsg : err.message || "Failed to load");
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [apiUrl, getAuthHeaders, id]
  );

  const notifyBoardRefresh = useCallback(
    async (updatedProject = null) => {
      const projectPayload =
        updatedProject && typeof updatedProject === "object" && updatedProject.id != null
          ? updatedProject
          : null;
      try {
        const bc = new BroadcastChannel("project-board-sync");
        bc.postMessage({
          type: "project-updated",
          projectId: String(id),
          ...(projectPayload ? { project: projectPayload } : {}),
        });
        bc.close();
      } catch {
        /* ignore */
      }
      await loadProject(true);
    },
    [id, loadProject]
  );

  const applyPatchedProjectLocally = useCallback((patchData) => {
    const row = projectRowFromPatchResponse(patchData);
    if (!row) return null;
    setData((prev) => {
      if (!prev) return { ...row, acf_domains: row.acf_domains || {} };
      return {
        ...prev,
        ...row,
        acf_domains: row.acf_domains != null ? row.acf_domains : prev.acf_domains,
      };
    });
    const domains = readCommercialDomainsForRow(row, row.acf_domains);
    setCommercialDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [COMMERCIAL_DOMAINS.SUMMARY]: {
          ...(prev[COMMERCIAL_DOMAINS.SUMMARY] || {}),
          ...(domains[COMMERCIAL_DOMAINS.SUMMARY] || {}),
        },
      };
    });
    setCommercialEditBaseline((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [COMMERCIAL_DOMAINS.SUMMARY]: {
          ...(prev[COMMERCIAL_DOMAINS.SUMMARY] || {}),
          ...(domains[COMMERCIAL_DOMAINS.SUMMARY] || {}),
        },
      };
    });
    return row;
  }, []);

  useEffect(() => {
    if (!data || !isCvinExpansion(data) || !isPluginEnabled(PLUGIN_IDS.CONTRACTS)) {
      setContractDerivedConstruction(null);
      return undefined;
    }
    let cancelled = false;
    fetchProjectContractFields(id, { apiUrl, getAuthHeaders })
      .then((res) => {
        if (!cancelled) setContractDerivedConstruction(res);
      })
      .catch(() => {
        if (!cancelled) setContractDerivedConstruction(null);
      });
    return () => {
      cancelled = true;
    };
  }, [data, id, apiUrl, getAuthHeaders]);

  useEffect(() => {
    if (!data || !isCvinExpansion(data) || !isPluginEnabled(PLUGIN_IDS.INTERCONNECTS)) {
      setInterconnectFields(null);
      return undefined;
    }
    if (!expansionProjectHasCdt(data)) {
      setInterconnectFields(null);
      return undefined;
    }
    let cancelled = false;
    fetchProjectInterconnectFields(id, { apiUrl, getAuthHeaders })
      .then((res) => {
        if (!cancelled) setInterconnectFields(res);
      })
      .catch(() => {
        if (!cancelled) setInterconnectFields(null);
      });
    return () => {
      cancelled = true;
    };
  }, [data, id, apiUrl, getAuthHeaders]);

  useEffect(() => {
    if (!data || !isCvinExpansion(data)) {
      setPermitCoverage(null);
      return undefined;
    }
    let cancelled = false;
    fetchProjectPermitCoverage(id, { apiUrl, getAuthHeaders })
      .then((res) => {
        if (!cancelled) setPermitCoverage(res);
      })
      .catch(() => {
        if (!cancelled) setPermitCoverage(null);
      });
    return () => {
      cancelled = true;
    };
  }, [data, id, apiUrl, getAuthHeaders]);

  const closePhaseEmailModal = useCallback(() => {
    if (phaseEmailSending) return;
    setPhaseEmailModal(null);
    setPhaseEmailPreview(null);
    setPhaseEmailPreviewLoading(false);
    setPhaseEmailPreviewError("");
    setPhaseEmailSendError("");
  }, [phaseEmailSending]);

  const advancePhaseAfterEmail = useCallback(
    async (rowForPhase, next) => {
      setPhaseAdvanceError("");
      const base = apiBase(apiUrl);
      const body = buildAdvancePhasePatch(rowForPhase, next);
      const patchRes = await axios.patch(`${base}/projects/${id}`, body, { headers: getAuthHeaders() });
      emitMarketplaceRewardFromResponse(patchRes.data);
      const mpNote = describePhaseMarketplaceRewardResult(patchRes.data?.marketplacePhaseRewards);
      if (mpNote) setPhaseMarketplaceNote(mpNote);
      maybeSyncExpansionPhaseToAgol(rowForPhase, patchRes.data || null);
      const applied = applyPatchedProjectLocally(patchRes.data);
      await notifyBoardRefresh(applied);
    },
    [apiUrl, applyPatchedProjectLocally, getAuthHeaders, id, notifyBoardRefresh]
  );

  const sendPhaseCompleteEmail = useCallback(
    async (rowForPhase, cur, emailPayload) => {
      const base = apiBase(apiUrl);
      try {
        const em = await sendPhaseTriggerEmail(base, id, emailPayload, getAuthHeaders);
        const note = describePhaseEmailResult(em, null);
        if (note) setPhaseEmailNote(note);
      } catch (emailErr) {
        const note = describePhaseEmailResult(null, emailErr);
        if (note) setPhaseEmailNote(note);
        throw emailErr;
      }
    },
    [apiUrl, getAuthHeaders, id]
  );

  const onPhaseComplete = useCallback(async () => {
    if (!data) return;
    const { acf_domains: _acfDomains, ...rowForPhase } = data;
    const cur = displayPhaseForRow(rowForPhase).trim();
    const next = nextPhaseLabelForRow(rowForPhase, cur);
    if (!next) return;
    setPhaseAdvanceError("");
    setPhaseEmailNote("");
    setPhaseMarketplaceNote("");

    const emailPayload = isPluginEnabled(PLUGIN_IDS.EMAIL_TRIGGERS)
      ? buildPhaseCompleteEmailPayload(cur, rowForPhase)
      : null;
    const useEmailPreview =
      emailPayload && phaseCompleteEmailUsesPreview(emailPayload.triggerId);

    if (useEmailPreview) {
      setPhaseEmailModal({ rowForPhase, cur, next, emailPayload });
      setPhaseEmailPreview(null);
      setPhaseEmailPreviewError("");
      setPhaseEmailSendError("");
      setPhaseEmailPreviewLoading(true);
      try {
        const base = apiBase(apiUrl);
        const preview = await fetchPhaseTriggerEmailPreview(
          base,
          id,
          emailPayload.triggerId,
          getAuthHeaders
        );
        if (preview?.preview === false && preview?.reason === "plugin_disabled") {
          setPhaseAdvancing(true);
          try {
            await advancePhaseAfterEmail(rowForPhase, next);
          } catch (err) {
            const apiMsg = err.response?.data?.error;
            setPhaseAdvanceError(
              typeof apiMsg === "string" ? apiMsg : err.message || "Could not advance phase."
            );
          } finally {
            setPhaseAdvancing(false);
          }
          setPhaseEmailModal(null);
          return;
        }
        setPhaseEmailPreview(preview);
      } catch (err) {
        const apiMsg = err.response?.data?.error;
        setPhaseEmailPreviewError(
          typeof apiMsg === "string" ? apiMsg : err.message || "Could not load email preview."
        );
      } finally {
        setPhaseEmailPreviewLoading(false);
      }
      return;
    }

    setPhaseAdvancing(true);
    try {
      await advancePhaseAfterEmail(rowForPhase, next);
      if (emailPayload) {
        await sendPhaseCompleteEmail(rowForPhase, cur, emailPayload);
      }
    } catch (err) {
      const apiMsg = err.response?.data?.error;
      setPhaseAdvanceError(
        typeof apiMsg === "string" ? apiMsg : err.message || "Could not complete phase."
      );
    } finally {
      setPhaseAdvancing(false);
    }
  }, [
    advancePhaseAfterEmail,
    apiUrl,
    data,
    getAuthHeaders,
    id,
    sendPhaseCompleteEmail,
  ]);

  const onPhaseEmailModalSend = useCallback(
    async ({ to, subject, body }) => {
      if (!phaseEmailModal) return;
      const { rowForPhase, cur, next, emailPayload } = phaseEmailModal;
      setPhaseEmailSendError("");
      setPhaseEmailSending(true);
      let emailNote = "";
      try {
        await sendPhaseCompleteEmail(rowForPhase, cur, {
          ...emailPayload,
          to,
          subject,
          body,
        });
      } catch (err) {
        const apiMsg = err.response?.data?.error;
        emailNote =
          typeof apiMsg === "string" ? apiMsg : err.message || "Could not send email.";
        // Still advance the phase — email failure must not trap Networking Complete.
        setPhaseEmailNote(`Phase completed, but email was not sent: ${emailNote}`);
      }
      closePhaseEmailModal();
      setPhaseAdvancing(true);
      try {
        await advancePhaseAfterEmail(rowForPhase, next);
        if (!emailNote) {
          /* sendPhaseCompleteEmail already set phaseEmailNote on success/skip */
        }
      } catch (err) {
        const apiMsg = err.response?.data?.error;
        setPhaseAdvanceError(
          typeof apiMsg === "string" ? apiMsg : err.message || "Could not advance phase."
        );
      } finally {
        setPhaseAdvancing(false);
        setPhaseEmailSending(false);
      }
    },
    [advancePhaseAfterEmail, closePhaseEmailModal, phaseEmailModal, sendPhaseCompleteEmail]
  );

  const onPhaseEmailModalCompleteWithoutEmail = useCallback(async () => {
    if (!phaseEmailModal) return;
    const { rowForPhase, next } = phaseEmailModal;
    setPhaseEmailSendError("");
    setPhaseEmailSending(true);
    try {
      closePhaseEmailModal();
      setPhaseAdvancing(true);
      try {
        await advancePhaseAfterEmail(rowForPhase, next);
        setPhaseEmailNote("Phase completed without sending a notification email.");
      } catch (err) {
        const apiMsg = err.response?.data?.error;
        setPhaseAdvanceError(
          typeof apiMsg === "string" ? apiMsg : err.message || "Could not advance phase."
        );
      } finally {
        setPhaseAdvancing(false);
      }
    } finally {
      setPhaseEmailSending(false);
    }
  }, [advancePhaseAfterEmail, closePhaseEmailModal, phaseEmailModal]);

  const onCloseCommercialProject = useCallback(async () => {
    if (!data || isCvinExpansion(data)) return;
    setPhaseAdvanceError("");
    setPhaseEmailNote("");
    setPhaseMarketplaceNote("");
    setPhaseAdvancing(true);
    try {
      const base = apiBase(apiUrl);
      const body = buildAccountantCloseCommercialProjectPatch(data);
      const patchRes = await axios.patch(`${base}/projects/${id}`, body, { headers: getAuthHeaders() });
      emitMarketplaceRewardFromResponse(patchRes.data);
      const mpNote = describePhaseMarketplaceRewardResult(patchRes.data?.marketplacePhaseRewards);
      if (mpNote) setPhaseMarketplaceNote(mpNote);
      const applied = applyPatchedProjectLocally(patchRes.data);
      await notifyBoardRefresh(applied);
    } catch (err) {
      const apiMsg = err.response?.data?.error;
      setPhaseAdvanceError(
        typeof apiMsg === "string" ? apiMsg : err.message || "Could not close project."
      );
    } finally {
      setPhaseAdvancing(false);
    }
  }, [apiUrl, applyPatchedProjectLocally, data, getAuthHeaders, id, notifyBoardRefresh]);

  const onArchiveDeleteProject = useCallback(async () => {
    if (!data?.id) return;
    const wo =
      data.work_order != null && String(data.work_order).trim() !== ""
        ? String(data.work_order).trim()
        : null;
    const name = data.name != null ? String(data.name).trim() : "";
    const label = wo && name ? `project ${wo} (${name})` : wo ? `project ${wo}` : name || `project ${data.id}`;
    setArchiveError("");
    setArchivingProject(true);
    try {
      const result = await confirmAndArchiveDeleteProjects([data.id], { label });
      if (result.cancelled) return;
      navigate("/", { replace: true });
    } catch (err) {
      const apiMsg = err?.response?.data?.error;
      setArchiveError(
        typeof apiMsg === "string" ? apiMsg : err.message || "Could not archive project."
      );
    } finally {
      setArchivingProject(false);
    }
  }, [data, navigate]);

  /** Initial navigation and revisiting this route refresh project + changelog (includes new grid edits). */
  useEffect(() => {
    loadProject(false);
  }, [loadProject, location.key]);

  /** Grid saved while this project page is open in another tab → refetch changelog + embedded JSON. */
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return undefined;
    const bc = new BroadcastChannel("project-board-sync");
    bc.onmessage = (ev) => {
      if (ev?.data?.type !== "project-updated") return;
      if (String(ev.data.projectId) !== String(id)) return;
      loadProject(true);
    };
    return () => bc.close();
  }, [id, loadProject]);

  /** Plugins toggled (e.g. Change history) → refetch with current headers. */
  useEffect(() => {
    const fn = () => loadProject(true);
    window.addEventListener("pb-plugins-changed", fn);
    return () => window.removeEventListener("pb-plugins-changed", fn);
  }, [loadProject]);

  /** Hide department tabs that do not apply; fall back to Overview if the active tab was removed. */
  useEffect(() => {
    if (!data || isCvinExpansion(data)) return;
    const email = getViewerEmailFromStorage();
    const domains = readCommercialDomainsForRow(data, data.acf_domains);
    const tabs = filterLandingTabsForViewer(visibleLandingTabs(domains), email, { commercial: true });
    if (!tabs.some((t) => t.id === activeTab)) {
      const fallback = primaryEditTabForPersona(email);
      const nextTab =
        (fallback && tabs.some((t) => t.id === fallback) && fallback) || tabs[0]?.id;
      setActiveTab(nextTab ?? "summary");
    }
  }, [data, activeTab]);

  /** Accountants land on the Accounting tab when opening a commercial project. */
  useEffect(() => {
    if (!data || isCvinExpansion(data)) return;
    const email = getViewerEmailFromStorage();
    if (resolveViewerLandingPersona(email) !== "accountant") return;
    const domains = readCommercialDomainsForRow(data, data.acf_domains);
    const tabs = filterLandingTabsForViewer(visibleLandingTabs(domains), email, { commercial: true });
    if (tabs.some((t) => t.id === "accounting")) {
      setActiveTab("accounting");
    }
  }, [data?.id]);

  /** Splicers and Splicing Managers land on the Fiber tab when opening a project. */
  useEffect(() => {
    if (!data) return;
    const email = getViewerEmailFromStorage();
    const persona = resolveViewerLandingPersona(email);
    if (persona !== "splicer" && persona !== "splicingManager") return;
    const domains = isCvinExpansion(data)
      ? readExpansionEditDomains(data, data.acf_domains)
      : readCommercialDomainsForRow(data, data.acf_domains);
    const tabs = filterLandingTabsForViewer(
      isCvinExpansion(data) ? visibleExpansionLandingTabs(domains) : visibleLandingTabs(domains),
      email,
      { commercial: !isCvinExpansion(data) }
    );
    if (tabs.some((t) => t.id === "fiber")) {
      setActiveTab("fiber");
    }
  }, [data?.id]);

  /** Network Technician users land on the Networking tab when opening a project. */
  useEffect(() => {
    if (!data) return;
    const email = getViewerEmailFromStorage();
    if (resolveViewerLandingPersona(email) !== "networkTech") return;
    const domains = isCvinExpansion(data)
      ? readExpansionEditDomains(data, data.acf_domains)
      : readCommercialDomainsForRow(data, data.acf_domains);
    const tabs = filterLandingTabsForViewer(
      isCvinExpansion(data) ? visibleExpansionLandingTabs(domains) : visibleLandingTabs(domains),
      email,
      { commercial: !isCvinExpansion(data) }
    );
    if (tabs.some((t) => t.id === "networking")) {
      setActiveTab("networking");
    }
  }, [data?.id]);

  useEffect(() => {
    if (!data || !isCvinExpansion(data)) return;
    const domains = readExpansionEditDomains(data, data.acf_domains);
    const tabs = filterLandingTabsForViewer(visibleExpansionLandingTabs(domains), getViewerEmailFromStorage());
    if (!tabs.some((t) => t.id === activeTab)) {
      setActiveTab(tabs[0]?.id ?? "summary");
    }
  }, [data, activeTab]);

  /** File uploads save immediately; merge into draft + baseline so text pending edits still show in the banner. */
  const onCommercialFileSynced = useCallback(async (uploadResult) => {
    if (!uploadResult?.project?.acf_groups_json) return;
    const server = readCommercialDomainsForRow(
      uploadResult.project,
      uploadResult.project?.acf_domains
    );
    setData(uploadResult.project);
    setCommercialDraft((prev) => mergeCommercialFileFieldsFromServer(prev || {}, server));
    setCommercialEditBaseline((prev) =>
      prev ? mergeCommercialFileFieldsFromServer(prev, server) : prev
    );
    if (!isCvinExpansion(uploadResult.project)) {
      try {
        const base = apiBase(apiUrl);
        const body = buildCommercialPatchBody(uploadResult.project.acf_groups_json, server);
        const { data: patched } = await axios.patch(`${base}/projects/${id}`, body, {
          headers: getAuthHeaders(),
        });
        if (patched?.acf_groups_json) {
          setData(patched);
        }
      } catch {
        /* subphase sync is best-effort after file upload */
      }
    } else {
      const expServer = readExpansionEditDomains(uploadResult.project, uploadResult.project?.acf_domains);
      setExpansionDraft((prev) => mergeCommercialFileFieldsFromServer(prev || {}, expServer));
      setExpansionEditBaseline((prev) =>
        prev ? mergeCommercialFileFieldsFromServer(prev, expServer) : prev
      );
    }
    try {
      const bc = new BroadcastChannel("project-board-sync");
      bc.postMessage({ type: "project-updated", projectId: String(id) });
      bc.close();
    } catch {
      /* ignore */
    }
  }, [apiUrl, getAuthHeaders, id]);

  const saveCommercialDraft = useCallback(async () => {
    if (!commercialDraft || !data) return;
    setCommercialSaving(true);
    setCommercialSaveError("");
    try {
      const base = apiBase(apiUrl);
      const body = buildCommercialPatchBody(data.acf_groups_json, commercialDraft);
      const { data: patched } = await axios.patch(`${base}/projects/${id}`, body, {
        headers: getAuthHeaders(),
      });
      const applied = applyPatchedProjectLocally(patched);
      const installs = repeaterToArray(commercialDraft?.[COMMERCIAL_DOMAINS.NETWORKING]?.installations);
      const needsInstallNotify = installs.some(
        (row) => installationRowIsSchedulable(row) && !row.schedule_email_sent
      );
      if (needsInstallNotify) {
        try {
          await notifyNetworkingInstallationEmails({ projectId: id, getAuthHeaders, apiUrl });
        } catch {
          /* email is best-effort after save */
        }
      }
      await notifyBoardRefresh(applied || patched);
    } catch (err) {
      const apiMsg = err?.response?.data?.error;
      setCommercialSaveError(
        typeof apiMsg === "string" ? apiMsg : err?.message || "Could not save changes."
      );
      throw err;
    } finally {
      setCommercialSaving(false);
    }
  }, [
    apiUrl,
    applyPatchedProjectLocally,
    commercialDraft,
    data,
    getAuthHeaders,
    id,
    notifyBoardRefresh,
  ]);

  const saveOverviewNotes = useCallback(
    async (nextNotes) => {
      if (!data || isCvinExpansion(data)) return;
      setCommercialSaving(true);
      setCommercialSaveError("");
      try {
        const domains = readCommercialDomainsForRow(data, data.acf_domains);
        const draft = {
          ...domains,
          [COMMERCIAL_DOMAINS.SUMMARY]: {
            ...(domains[COMMERCIAL_DOMAINS.SUMMARY] || {}),
            notes: nextNotes != null ? String(nextNotes) : "",
          },
        };
        const base = apiBase(apiUrl);
        const body = buildCommercialPatchBody(data.acf_groups_json, draft);
        const { data: patched } = await axios.patch(`${base}/projects/${id}`, body, {
          headers: getAuthHeaders(),
        });
        const applied = applyPatchedProjectLocally(patched);
        await notifyBoardRefresh(applied || patched);
      } catch (err) {
        const apiMsg = err?.response?.data?.error;
        setCommercialSaveError(
          typeof apiMsg === "string" ? apiMsg : err?.message || "Could not save notes."
        );
        throw err;
      } finally {
        setCommercialSaving(false);
      }
    },
    [apiUrl, applyPatchedProjectLocally, data, getAuthHeaders, id, notifyBoardRefresh]
  );

  const saveExpansionDraft = useCallback(async () => {
    if (!expansionDraft || !data) return;
    setCommercialSaving(true);
    setCommercialSaveError("");
    try {
      const base = apiBase(apiUrl);
      const beforeRow = data;
      const body = buildExpansionPatchBody(data.acf_groups_json, expansionDraft);
      const { data: patched } = await axios.patch(`${base}/projects/${id}`, body, {
        headers: getAuthHeaders(),
      });
      if (patched) setData(patched);
      maybeSyncExpansionPhaseToAgol(beforeRow, patched || null);
      notifyBoardRefresh();
    } catch (err) {
      const apiMsg = err?.response?.data?.error;
      setCommercialSaveError(
        typeof apiMsg === "string" ? apiMsg : err?.message || "Could not save changes."
      );
      throw err;
    } finally {
      setCommercialSaving(false);
    }
  }, [apiUrl, data, expansionDraft, getAuthHeaders, id, notifyBoardRefresh]);

  const pendingEditCount = useMemo(() => {
    if (!detailsEditMode) return 0;
    if (data && isCvinExpansion(data)) {
      if (!expansionDraft || !expansionEditBaseline) return 0;
      return countExpansionPendingEdits(expansionEditBaseline, expansionDraft);
    }
    if (!commercialDraft || !commercialEditBaseline) return 0;
    return countCommercialPendingEdits(commercialEditBaseline, commercialDraft);
  }, [commercialDraft, commercialEditBaseline, data, detailsEditMode, expansionDraft, expansionEditBaseline]);

  const toggleDetailsEditMode = useCallback(async () => {
    const row = data;
    const commercial = row && !isCvinExpansion(row);
    const expansion = row && isCvinExpansion(row);
    if (detailsEditMode) {
      if (commercial && commercialDraft) {
        try {
          await saveCommercialDraft();
          setDetailsEditMode(false);
          setCommercialEditBaseline(null);
        } catch {
          /* commercialSaveError set */
        }
        return;
      }
      if (expansion && expansionDraft) {
        try {
          await saveExpansionDraft();
          setDetailsEditMode(false);
          setExpansionEditBaseline(null);
        } catch {
          /* commercialSaveError set */
        }
        return;
      }
      setDetailsEditMode(false);
      setCommercialEditBaseline(null);
      setExpansionEditBaseline(null);
      return;
    }
    if (commercial && row) {
      const domains = readCommercialDomainsForRow(row, row.acf_domains);
      setCommercialDraft(domains);
      setCommercialEditBaseline(domains);
    }
    if (expansion && row) {
      const domains = readExpansionEditDomains(row, row.acf_domains);
      setExpansionDraft(domains);
      setExpansionEditBaseline(domains);
    }
    const email = getViewerEmailFromStorage();
    const canEditAll = hasEffectiveAdminPrivileges() || hasViewerOperationalAdminAccess(email);
    const editableTabs = !canEditAll ? editableTabsForPersona(email) : null;
    if (editableTabs && !editableTabs.has(activeTab)) {
      const domains =
        commercial && row
          ? readCommercialDomainsForRow(row, row.acf_domains)
          : expansion && row
            ? readExpansionEditDomains(row, row.acf_domains)
            : null;
      const tabs = domains
        ? filterLandingTabsForViewer(
            commercial ? visibleLandingTabs(domains) : visibleExpansionLandingTabs(domains),
            email,
            { commercial: Boolean(commercial) }
          )
        : [];
      const fallbackTab = primaryEditTabForPersona(email);
      const nextTab =
        (fallbackTab && tabs.some((t) => t.id === fallbackTab) && fallbackTab) || tabs[0]?.id;
      if (nextTab) setActiveTab(nextTab);
    }
    setCommercialSaveError("");
    setDetailsEditMode(true);
  }, [
    activeTab,
    commercialDraft,
    data,
    detailsEditMode,
    expansionDraft,
    saveCommercialDraft,
    saveExpansionDraft,
  ]);

  if (loading) {
    return (
      <div className="project-detail-shell phase-section-default">
        <p className="project-detail-muted">Loading…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="project-detail-shell phase-section-default">
        <Link to="/" className="project-detail-back">← Back to board</Link>
        <p className="project-detail-error">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const { acf_domains: acfDomains, ...projectRow } = data;
  // Work-order URLs (e.g. /project/7712.06) resolve via API then canonicalize to /project/:id.
  if (projectRow.id != null && String(projectRow.id) !== String(id)) {
    return (
      <Navigate
        to={`/project/${projectRow.id}${location.search}${location.hash || ""}`}
        replace
      />
    );
  }
  const isExpansionProjectEarly = isCvinExpansion(projectRow);
  const woEarly = isExpansionProjectEarly
    ? formatExpansionWorkOrderForDisplay(projectRow)
    : projectRow.work_order != null
      ? String(projectRow.work_order).trim()
      : "";
  if (
    isCommercialWorkOrderGroupParent(projectRow, {
      [woEarly]: Number(projectRow.sub_work_order_count ?? 0),
    })
  ) {
    return <Navigate to={`/work-order/${woEarly}`} replace />;
  }

  const changeHistoryEnabled = isPluginEnabled(PLUGIN_IDS.CHANGE_HISTORY);
  const projectCommentsEnabled =
    isPluginEnabled(PLUGIN_IDS.PROJECT_COMMENTS) && canAccessProjectComments();
  const embeddedLogItems = changeHistoryEnabled
    ? gatherEmbeddedChangeLogItems(projectRow, acfDomains)
    : [];
  const embeddedSorted = sortEmbeddedByDateDesc(embeddedLogItems);
  const auditSorted = sortAuditByDateDesc(changeLog);
  const changelogTotal = embeddedSorted.length + auditSorted.length;
  const arcgisApiKey = (process.env.REACT_APP_ARCGIS_API_KEY || "").trim();
  const isExpansionProject = isCvinExpansion(projectRow);
  const wo = isExpansionProject
    ? formatExpansionWorkOrderForDisplay(projectRow)
    : projectRow.work_order != null
      ? String(projectRow.work_order).trim()
      : "";
  const workOrderBase =
    projectRow.work_order_base != null && String(projectRow.work_order_base).trim() !== ""
      ? String(projectRow.work_order_base).trim()
      : wo.includes(".")
        ? wo.split(".")[0]
        : wo;
  const isMultisiteChild = Boolean(wo.includes(".") && workOrderBase);
  const showWorkOrderGroupLink = !isExpansionProject && isMultisiteChild;
  const showExpansionSegmentLink =
    isExpansionProject && workOrderBase && String(workOrderBase).trim() !== "";
  const cust = projectRow.customer != null ? String(projectRow.customer).trim() : "";
  const projName = projectRow.name != null ? String(projectRow.name).trim() : "";
  // Expansion subsegments inherit customer "CVIN Expansion"; the title should show the
  // project name the user entered, not the shared group customer.
  const titleSecondary = isExpansionProject ? projName || cust : cust || projName;
  const heroTitle =
    wo && titleSecondary ? `${wo} – ${titleSecondary}` : wo || titleSecondary || `Project ${id}`;
  const badgeChoices = projectDetailChoicesLabel(projectRow);
  const badgeSpeedRaw = summaryGroup(projectRow)?.service_speed;
  const badgeSpeed = serviceSpeedLabel(badgeSpeedRaw);
  const badgeStatus =
    projectRow.status != null && String(projectRow.status).trim() !== ""
      ? String(projectRow.status).trim()
      : "";
  const badgePhase = projectDetailPhaseBadge(projectRow);
  const gridPhaseLabel = displayPhaseForRow(projectRow).trim();
  const nextPhaseLabel = nextPhaseLabelForRow(projectRow, gridPhaseLabel);
  const invoicedLine = formatInvoicedHeaderLine(projectRow);
  const summaryKeys = [
    "work_order",
    "name",
    "customer",
    "status",
    "phase_label",
    "project_type",
    "subtype",
    "service_location",
    "a_location",
    "z_location",
    "sales_rep",
    "mrc",
    "non_recurring_charge",
    "term_in_months",
    "project_notes",
  ];
  const summaryRows = summaryKeys
    .filter((k) => Object.prototype.hasOwnProperty.call(projectRow, k))
    .map((k) => [k, projectRow[k]]);
  const domains = objectEntriesSorted(acfDomains);
  const byToken = (tokens) =>
    domains.filter(([k]) => tokens.some((t) => String(k).toLowerCase().includes(t)));
  const summaryDomains = domains.filter(([k]) => {
    const n = String(k).toLowerCase();
    return n.includes("summary");
  });
  const engineeringPermittingDomains = byToken(["engineering", "permit"]);
  const constructionDomains = byToken(["construction", "expansion", "segment"]);
  const fiberDomains = byToken(["fiber"]);
  const networkingDomains = byToken(["network"]);
  const accountingDomains = byToken(["account"]);
  const recentActivity = embeddedSorted.slice(0, 6).map(({ entry, key }) => ({
    key,
    field: entry.field != null ? String(entry.field) : "Update",
    user: entry.user != null ? String(entry.user) : "",
    date: entry.date != null ? String(entry.date) : "",
  }));
  const tabDomainLists = {
    engineering: engineeringPermittingDomains,
    construction: constructionDomains,
    fiber: fiberDomains,
    networking: networkingDomains,
    accounting: accountingDomains,
  };
  const useCommercialLanding = !isCvinExpansion(projectRow);
  const commercialDomains = useCommercialLanding
    ? readCommercialDomainsForRow(projectRow, acfDomains)
    : null;
  const expansionDomains = useCommercialLanding
    ? null
    : readExpansionEditDomains(projectRow, acfDomains);
  const viewerEmail = getViewerEmailFromStorage();
  const landingTabs = filterLandingTabsForViewer(
    useCommercialLanding
      ? visibleLandingTabs(commercialDomains)
      : visibleExpansionLandingTabs(expansionDomains),
    viewerEmail,
    { commercial: useCommercialLanding }
  );
  const showPhaseCompleteBtn = useCommercialLanding
    ? canShowPhaseCompleteButton(
        projectRow,
        commercialDomains,
        gridPhaseLabel,
        nextPhaseLabel,
        viewerEmail
      )
    : // TEMPORARY: expansion Construction complete no longer requires as-builts or pending_fiber.
      Boolean(gridPhaseLabel && nextPhaseLabel);
  const mapLocations = resolveCommercialMapLocations(projectRow, commercialDomains);
  const mapUrl = arcgisSearchUrl(
    mapLocations.hasAz ? mapLocations.aLocation : mapLocations.serviceLocation
  );
  const heroAzLocations = mapLocations.hasAz
    ? { a: mapLocations.aLocation, z: mapLocations.zLocation }
    : null;
  const heroLocation = heroAzLocations ? null : mapLocations.serviceLocation;
  const heroProjectType =
    projectRow?.project_type != null && String(projectRow.project_type).trim() !== ""
      ? String(projectRow.project_type).trim()
      : commercialDomains?.project_summary?.type != null
        ? String(commercialDomains.project_summary.type).trim()
        : "";
  const heroMrcTag = landingAmortizedMrcTag(projectRow, commercialDomains);
  const heroIncidentTag = isCommercialIncidentFlag(
    commercialDomains?.[COMMERCIAL_DOMAINS.SUMMARY]?.incident ?? projectRow?.incident
  )
    ? "Incident"
    : "";
  const heroMetaTags = [
    heroProjectType,
    heroIncidentTag,
    heroIncidentTag ? "" : badgeChoices,
    heroIncidentTag ? "" : heroMrcTag,
  ].filter(Boolean);
  const heroPhaseLabel = gridPhaseLabel || badgePhase;
  const heroPhaseStyleKey = resolvePhaseStyleKey(heroPhaseLabel);
  const heroPhaseTagBg = heroPhaseStyleKey ? PHASE_CELL_BACKGROUND[heroPhaseStyleKey] : null;
  const statusComplete = badgeStatus.toLowerCase() === "complete";

  const canEditAllDetailFields = hasEffectiveAdminPrivileges() || hasViewerOperationalAdminAccess(viewerEmail);
  const accountantViewer = isAccountantViewer(viewerEmail);
  const splicerViewer = isSplicerViewer(viewerEmail);
  const canEditExpansionRoleFields =
    isExpansionProject && canEditExpansionRestrictedFields(viewerEmail) && !accountantViewer && !splicerViewer;
  const canEditPermittingFields = canEditPermittingTabFields(viewerEmail);
  const canEditSalesOverview =
    useCommercialLanding &&
    !canEditPermittingFields &&
    canEditCommercialOverviewFields(viewerEmail);
  const canEditEngineeringFields = canEditEngineeringTabFields(viewerEmail);
  const canEditOspSiteWalkFiles =
    useCommercialLanding && canEditOspEngineeringSiteWalkFiles(viewerEmail);
  const canEditInspectorDailyProgress = canEditInspectorDailyProgressReports(viewerEmail);
  const canEditConstructionUploads = canEditConstructionTabUploads(viewerEmail);
  const canEditNetworkingFields = canEditNetworkingTabFields(viewerEmail);
  const canEditFiberFields = canEditFiberTabFields(viewerEmail);
  const canEditAccountingFields = canEditAccountingTabFields(viewerEmail);
  const canShowCloseProjectBtn =
    useCommercialLanding &&
    commercialDomains &&
    canShowAccountantCloseProjectButton(commercialDomains, viewerEmail);
  const canShowDetailEditButton =
    (!splicerViewer || canEditFiberFields) &&
    (canEditAllDetailFields ||
      canEditExpansionRoleFields ||
      canEditPermittingFields ||
      canEditSalesOverview ||
      canEditEngineeringFields ||
      canEditOspSiteWalkFiles ||
      canEditInspectorDailyProgress ||
      canEditConstructionUploads ||
      canEditNetworkingFields ||
      canEditFiberFields ||
      canEditAccountingFields) &&
    (!accountantViewer || canEditAccountingFields);
  const canDeleteProject = hasEffectiveAdminPrivileges();
  const showFullDetailFields =
    detailsEditMode &&
    (canEditAllDetailFields ||
      canEditExpansionRoleFields ||
      (canEditPermittingFields && (activeTab === "permitting" || activeTab === "summary")) ||
      (canEditSalesOverview && activeTab === "summary") ||
      (canEditEngineeringFields && activeTab === "engineering") ||
      (canEditOspSiteWalkFiles && activeTab === "engineering") ||
      (canEditInspectorDailyProgress && activeTab === "construction") ||
      (canEditConstructionUploads && activeTab === "construction") ||
      (canEditNetworkingFields && activeTab === "networking") ||
      (canEditFiberFields && activeTab === "fiber") ||
      (canEditAccountingFields && activeTab === "accounting"));
  const isMobileLanding = isMobile && !showFullDetailFields;
  const shellClass =
    (useCommercialLanding
      ? "project-detail-shell project-detail-shell--landing"
      : `${getPhaseDetailShellClassName(projectRow)} project-detail-shell--landing`) +
    (isMobileLanding ? " project-detail-shell--mobile-landing" : "");
  const inspectorDailyProgressEdit =
    !canEditAllDetailFields && canEditInspectorDailyProgress && activeTab === "construction";
  const constructionAsbuiltEdit =
    !canEditAllDetailFields && canEditConstructionUploads && activeTab === "construction";
  const networkingTabRoleEdit =
    !canEditAllDetailFields && canEditNetworkingFields && activeTab === "networking";
  const permittingOverviewEdit =
    !canEditAllDetailFields && canEditPermittingFields && activeTab === "summary";
  const salesOverviewEdit =
    !canEditAllDetailFields && canEditSalesOverview && activeTab === "summary";
  const showLinkedFiberFiles =
    canViewNetworkingTabFiberFiles(viewerEmail) && activeTab === "networking";
  const expansionFullAdminEdit = canEditAllDetailFields;
  const acfFileEditing = showFullDetailFields
    ? {
        projectId: id,
        getAuthHeaders,
        onUpdated: notifyBoardRefresh,
      }
    : null;
  const renderDomainBlock = (list) =>
    list.length > 0 ? (
      list.map(([domainKey, payload]) => (
        <div key={domainKey} className="project-detail-domain-block">
          <h3 className="project-detail-domain-heading">{domainDisplayTitle(domainKey)}</h3>
          <ProjectPayloadView
            data={payload}
            pathPrefix={[domainKey]}
            fileEditing={acfFileEditing}
          />
        </div>
      ))
    ) : (
      <p className="project-detail-muted">No data in this section yet.</p>
    );

  const renderTabPreview = (list) =>
    list.length > 0 ? (
      <div className="project-dashboard-domain-grid">
        {list.map(([domainKey, payload]) => (
          <DomainPreviewCard key={domainKey} domainKey={domainKey} payload={payload} />
        ))}
      </div>
    ) : (
      <p className="project-detail-muted">No data in this section yet.</p>
    );

  const pageContent = (
    <>
      <Link to="/" className="project-detail-back project-landing-back">← Back to board</Link>
      {showWorkOrderGroupLink ? (
        <p className="project-detail-muted project-landing-parent-link">
          Sub-work order —{" "}
          <Link to={`/work-order/${workOrderBase}`}>View parent order {workOrderBase}</Link>
        </p>
      ) : null}
      {showExpansionSegmentLink ? (
        <p className="project-detail-muted project-landing-parent-link">
          Segment {workOrderBase} —{" "}
          <Link to={`/expansion/segment/${encodeURIComponent(workOrderBase)}`}>
            View segment dashboard
          </Link>
          {wo.includes(".") ? (
            <>
              {" "}
              (subsegment <strong>{wo}</strong>)
            </>
          ) : null}
        </p>
      ) : null}
      <header className="project-landing-hero">
        <div className="project-landing-hero-main">
          <div className="project-landing-brand-row">
            <div className="project-landing-logo" aria-hidden>
              <LandingIcon name="circuit" />
            </div>
            <div className="project-landing-brand-copy">
              <div className="project-landing-title-row">
                <h1 className="project-landing-title">{heroTitle}</h1>
                {badgeStatus ? (
                  <span
                    className={`project-landing-status-pill${
                      statusComplete ? " project-landing-status-pill--complete" : ""
                    }`}
                  >
                    {badgeStatus}
                  </span>
                ) : null}
              </div>
              {heroAzLocations ? (
                <div className="project-landing-location project-landing-location--az">
                  <LandingIcon name="pin" className="project-landing-location-icon" />
                  <div className="project-landing-location-az">
                    <span>
                      <span className="project-landing-location-az-label">A</span>
                      {heroAzLocations.a}
                    </span>
                    <span>
                      <span className="project-landing-location-az-label">Z</span>
                      {heroAzLocations.z}
                    </span>
                  </div>
                </div>
              ) : heroLocation ? (
                <p className="project-landing-location">
                  <LandingIcon name="pin" className="project-landing-location-icon" />
                  {heroLocation}
                </p>
              ) : null}
              {heroPhaseLabel || heroMetaTags.length > 0 ? (
                <div className="project-landing-tags" aria-label="Project attributes">
                  {heroPhaseLabel ? (
                    <span
                      className={`project-landing-tag project-landing-tag--phase${
                        heroPhaseTagBg ? "" : " project-landing-tag--phase-default"
                      }`}
                      style={
                        heroPhaseTagBg
                          ? {
                              backgroundColor: heroPhaseTagBg,
                              borderColor: heroPhaseTagBg,
                              color: "#fff",
                            }
                          : undefined
                      }
                    >
                      {heroPhaseLabel}
                    </span>
                  ) : null}
                  {heroMetaTags.map((tag) => (
                    <span key={tag} className="project-landing-tag">
                      {tag}
                    </span>
                  ))}
                </div>
              ) : null}
              {phaseAdvanceError ? (
                <p className="project-detail-error project-detail-hero-phase-error" role="alert">
                  {phaseAdvanceError}
                </p>
              ) : null}
              {phaseEmailNote ? (
                <p className="project-detail-muted project-detail-hero-phase-note" role="status">
                  {phaseEmailNote}
                </p>
              ) : null}
              {phaseMarketplaceNote ? (
                <p className="project-detail-muted project-detail-hero-phase-note" role="status">
                  {phaseMarketplaceNote}
                </p>
              ) : null}
              {commercialSaveError ? (
                <p className="project-detail-error project-detail-hero-phase-error" role="alert">
                  {commercialSaveError}
                </p>
              ) : null}
              {archiveError ? (
                <p className="project-detail-error project-detail-hero-phase-error" role="alert">
                  {archiveError}
                </p>
              ) : null}
            </div>
          </div>
        </div>
        <div className="project-landing-hero-aside">
          {showPhaseCompleteBtn || canShowDetailEditButton || canShowCloseProjectBtn || canDeleteProject ? (
            <div className="project-landing-hero-actions">
              {canShowCloseProjectBtn ? (
                <button
                  type="button"
                  className="project-landing-phase-complete-btn"
                  disabled={phaseAdvancing}
                  onClick={onCloseCommercialProject}
                >
                  {phaseAdvancing ? "Saving…" : "Close Project"}
                </button>
              ) : null}
              {showPhaseCompleteBtn ? (
                <button
                  type="button"
                  className="project-landing-phase-complete-btn"
                  disabled={phaseAdvancing || phaseEmailSending || Boolean(phaseEmailModal)}
                  onClick={onPhaseComplete}
                >
                  {phaseAdvancing ? "Saving…" : `${gridPhaseLabel} Complete`}
                </button>
              ) : null}
              {canShowDetailEditButton ? (
                <button
                  type="button"
                  className="project-landing-edit-btn"
                  disabled={commercialSaving}
                  onClick={toggleDetailsEditMode}
                >
                  <LandingIcon name="pencil" className="project-landing-edit-btn-icon" />
                  {commercialSaving
                    ? "Saving…"
                    : detailsEditMode
                      ? "Save & done"
                      : "Edit project"}
                </button>
              ) : null}
              {canDeleteProject ? (
                <button
                  type="button"
                  className="project-landing-delete-btn"
                  disabled={archivingProject || phaseAdvancing || commercialSaving}
                  onClick={() => void onArchiveDeleteProject()}
                >
                  {archivingProject ? "Archiving…" : "Delete project"}
                </button>
              ) : null}
            </div>
          ) : null}
          {invoicedLine ? (
            <p className="project-landing-invoiced">
              <LandingIcon name="calendar" className="project-landing-invoiced-icon" />
              {invoicedLine}
            </p>
          ) : null}
        </div>
      </header>

      <nav className="project-detail-tabs project-landing-tabs" aria-label="Project sections">
        {landingTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`project-detail-tab${activeTab === tab.id ? " project-detail-tab--active" : ""}`}
            aria-selected={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {showFullDetailFields && pendingEditCount > 0 ? (
        <div className="commercial-pending-edits-banner" role="status" aria-live="polite">
          <span>
            <strong>{pendingEditCount}</strong> pending edit{pendingEditCount === 1 ? "" : "s"} — not saved
            yet
          </span>
          <span className="project-detail-muted" style={{ color: "#b91c1c", fontSize: 13 }}>
            Click <strong>Save &amp; done</strong> to apply
          </span>
        </div>
      ) : null}

      {useCommercialLanding && projectRow && commercialDomains && !isMobileLanding ? (
        <PhaseSchedulePrompt
          projectRow={projectRow}
          acfDomains={acfDomains}
          viewerEmail={viewerEmail}
          apiUrl={apiUrl}
          getAuthHeaders={getAuthHeaders}
          onSaved={notifyBoardRefresh}
        />
      ) : null}

      {!useCommercialLanding && projectRow && !isMobileLanding ? (
        <ReportedFootagePrompt
          projectRow={projectRow}
          acfDomains={acfDomains}
          apiUrl={apiUrl}
          getAuthHeaders={getAuthHeaders}
          onSaved={notifyBoardRefresh}
        />
      ) : null}

      <div className="project-detail-tab-panel">
        {isMobileLanding ? (
          <ProjectMobileLandingView
            projectRow={projectRow}
            domains={useCommercialLanding ? commercialDomains : expansionDomains}
            summaryDomainKey={
              useCommercialLanding ? COMMERCIAL_DOMAINS.SUMMARY : EXPANSION_SUMMARY_DOMAIN
            }
            mapUrl={mapUrl}
            arcgisApiKey={arcgisApiKey}
            contractDerivedConstruction={contractDerivedConstruction}
            isExpansion={!useCommercialLanding}
            projectId={id}
            getAuthHeaders={getAuthHeaders}
            apiUrl={apiUrl}
            permitCoverage={permitCoverage}
          />
        ) : useCommercialLanding && !showFullDetailFields ? (
          activeTab === "summary" ? (
            <ProjectSummaryDashboard
              projectRow={projectRow}
              commercialDomains={commercialDomains}
              overviewDomains={commercialDomains}
              summaryDomainKey={COMMERCIAL_DOMAINS.SUMMARY}
              mapUrl={mapUrl}
              arcgisApiKey={arcgisApiKey}
              recentActivity={recentActivity}
              notesEditable={
                canEditAllDetailFields || canEditCommercialOverviewFields(viewerEmail)
              }
              notesSaving={commercialSaving}
              onSaveNotes={saveOverviewNotes}
              onOpenActivity={
                changeHistoryEnabled
                  ? () => {
                      setChangelogOpen(true);
                      requestAnimationFrame(() => {
                        document.getElementById("project-changelog-panel")?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      });
                    }
                  : undefined
              }
            />
          ) : activeTab === "permitting" ? (
            <LandingPermittingTab domains={commercialDomains} />
          ) : (
            <CommercialSectionReadView
              domainKey={PROJECT_LANDING_TAB_TO_DOMAIN[activeTab]}
              domains={commercialDomains}
              showLinkedFiberFiles={showLinkedFiberFiles}
            />
          )
        ) : useCommercialLanding && showFullDetailFields ? (
          <div className="project-detail-tab-content">
            {commercialDraft ? (
            activeTab === "permitting" ? (
              <LandingPermittingTab
                domains={commercialDomains}
                draftDomains={commercialDraft}
                onDomainsChange={setCommercialDraft}
                editing
                projectId={id}
                getAuthHeaders={getAuthHeaders}
                onFileSynced={onCommercialFileSynced}
              />
            ) : (
            <CommercialProjectSectionPanel
              domainKey={PROJECT_LANDING_TAB_TO_DOMAIN[activeTab]}
              projectRow={projectRow}
              domains={commercialDraft}
              onDomainsChange={setCommercialDraft}
              editing
              fullAdminEdit={canEditAllDetailFields}
              tabRoleEdit={
                !canEditAllDetailFields &&
                ((canEditPermittingFields && (activeTab === "summary" || activeTab === "permitting")) ||
                  salesOverviewEdit ||
                  (canEditFiberFields && activeTab === "fiber") ||
                  (canEditAccountingFields && activeTab === "accounting"))
              }
              permittingOverviewEdit={permittingOverviewEdit}
              inspectorDailyProgressEdit={inspectorDailyProgressEdit}
              constructionAsbuiltEdit={constructionAsbuiltEdit}
              networkingTabRoleEdit={networkingTabRoleEdit}
              showLinkedFiberFiles={showLinkedFiberFiles}
              projectId={id}
              getAuthHeaders={getAuthHeaders}
              onFileSynced={onCommercialFileSynced}
            />
            )
            ) : (
              <p className="project-detail-muted">Preparing editor…</p>
            )}
          </div>
        ) : !useCommercialLanding && showFullDetailFields ? (
          <div className="project-detail-tab-content">
            {expansionDraft ? (
            activeTab === "permitting" ? (
              <LandingPermittingTab
                domains={expansionDomains}
                draftDomains={expansionDraft}
                onDomainsChange={setExpansionDraft}
                editing
                projectId={id}
                getAuthHeaders={getAuthHeaders}
                onFileSynced={onCommercialFileSynced}
                summaryDomainKey={EXPANSION_SUMMARY_DOMAIN}
                apiUrl={apiUrl}
                permitCoverage={permitCoverage}
                onPermitCoverageChange={setPermitCoverage}
              />
            ) : (
            <CommercialProjectSectionPanel
              domainKey={EXPANSION_LANDING_TAB_TO_DOMAIN[activeTab]}
              schemas={EXPANSION_DOMAIN_SCHEMAS}
              summaryDomainKey={EXPANSION_SUMMARY_DOMAIN}
              subphaseOptionsForPhaseFn={subphaseSelectOptionsForExpansionPhase}
              projectRow={projectRow}
              domains={expansionDraft}
              onDomainsChange={setExpansionDraft}
              editing
              fullAdminEdit={expansionFullAdminEdit}
              tabRoleEdit={
                !expansionFullAdminEdit &&
                ((canEditEngineeringFields && activeTab === "engineering") ||
                  (canEditPermittingFields &&
                    (activeTab === "summary" || activeTab === "permitting")) ||
                  (canEditFiberFields && activeTab === "fiber") ||
                  (canEditAccountingFields && activeTab === "accounting"))
              }
              permittingOverviewEdit={false}
              inspectorDailyProgressEdit={inspectorDailyProgressEdit}
              constructionAsbuiltEdit={constructionAsbuiltEdit}
              networkingTabRoleEdit={networkingTabRoleEdit}
              showLinkedFiberFiles={showLinkedFiberFiles}
              projectId={id}
              getAuthHeaders={getAuthHeaders}
              onFileSynced={onCommercialFileSynced}
              contractDerivedConstruction={contractDerivedConstruction}
              interconnectFields={interconnectFields}
              onInterconnectFieldsChange={setInterconnectFields}
              permitCoverage={permitCoverage}
              apiUrl={apiUrl}
            />
            )
            ) : (
              <p className="project-detail-muted">Preparing editor…</p>
            )}
          </div>
        ) : !useCommercialLanding && !showFullDetailFields ? (
          activeTab === "summary" ? (
            <ProjectSummaryDashboard
              projectRow={projectRow}
              commercialDomains={null}
              overviewDomains={expansionDomains}
              summaryDomainKey={EXPANSION_SUMMARY_DOMAIN}
              contractDerivedConstruction={contractDerivedConstruction}
              mapUrl={mapUrl}
              arcgisApiKey={arcgisApiKey}
              recentActivity={recentActivity}
              onOpenActivity={
                changeHistoryEnabled
                  ? () => {
                      setChangelogOpen(true);
                      requestAnimationFrame(() => {
                        document.getElementById("project-changelog-panel")?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      });
                    }
                  : undefined
              }
            />
          ) : activeTab === "permitting" ? (
            <LandingPermittingTab
              domains={expansionDomains}
              summaryDomainKey={EXPANSION_SUMMARY_DOMAIN}
              projectId={id}
              getAuthHeaders={getAuthHeaders}
              apiUrl={apiUrl}
              permitCoverage={permitCoverage}
            />
          ) : (
            <CommercialSectionReadView
              domainKey={EXPANSION_LANDING_TAB_TO_DOMAIN[activeTab]}
              domains={expansionDomains}
              schemas={EXPANSION_DOMAIN_SCHEMAS}
              summaryDomainKey={EXPANSION_SUMMARY_DOMAIN}
              contractDerivedConstruction={contractDerivedConstruction}
              interconnectFields={interconnectFields}
              projectRow={projectRow}
              showLinkedFiberFiles={showLinkedFiberFiles}
              projectId={id}
              permitCoverage={permitCoverage}
              apiUrl={apiUrl}
              getAuthHeaders={getAuthHeaders}
            />
          )
        ) : null}
      </div>

      {!showFullDetailFields && !useCommercialLanding ? (
        canShowDetailEditButton ? (
          <p className="project-detail-muted project-dashboard-footnote">
            {canEditAllDetailFields ? (
              <>
                Select <strong>Edit</strong> to change fields; click <strong>Save &amp; done</strong> when
                finished.
              </>
            ) : canEditPermittingFields ? (
              <>
                Use <strong>Edit project</strong> on the <strong>Overview</strong> or <strong>Permitting</strong> tab
                to update project fields and permits.
              </>
            ) : canEditInspectorDailyProgress ? (
              <>
                Open the <strong>Construction</strong> tab and use <strong>Edit project</strong> to add or
                update daily progress reports.
              </>
            ) : canEditOspSiteWalkFiles ? (
              <>
                Open the <strong>Engineering</strong> tab and use <strong>Edit project</strong> to upload or
                remove site walk and as-built files.
              </>
            ) : canEditConstructionUploads ? (
              <>
                Open the <strong>Construction</strong> tab and use <strong>Edit project</strong> to upload
                as-built and 811 / USA ticket files.
              </>
            ) : canEditEngineeringFields ? (
              <>
                Open the <strong>Engineering</strong> tab and use <strong>Edit project</strong> to update
                engineering fields.
              </>
            ) : canEditNetworkingFields ? (
              <>
                Open the <strong>Networking</strong> tab and use <strong>Edit project</strong> to update
                networking fields.
              </>
            ) : (
              <>
                Open the <strong>Construction</strong> tab to view contract fields synced from the{" "}
                <strong>Contracts</strong> page. Use <strong>Edit</strong> for daily progress and other
                construction data.
              </>
            )}
          </p>
        ) : (
          <p className="project-detail-muted project-dashboard-footnote">
            Ask an administrator if you need to edit attachments or domain fields.
          </p>
        )
      ) : null}

      {useCommercialLanding && (changeHistoryEnabled || projectCommentsEnabled) ? (
        <div className="project-landing-footer-stack">
          {changeHistoryEnabled ? (
      <section
        className="project-detail-section project-changelog-section project-detail-aux-card project-landing-footer-card"
        aria-label="Change history"
      >
        <button
          type="button"
          className="project-changelog-ribbon"
          aria-expanded={changelogOpen}
          aria-controls="project-changelog-panel"
          onClick={() => setChangelogOpen((o) => !o)}
        >
          <span className="project-changelog-ribbon-accent" aria-hidden />
          <span className="project-changelog-ribbon-main">
            <span className="project-changelog-ribbon-title">Change history</span>
            <span className="project-changelog-ribbon-meta">
              {changelogTotal === 0
                ? "No entries yet"
                : `${changelogTotal} ${changelogTotal === 1 ? "entry" : "entries"} · newest first`}
            </span>
          </span>
          <span className="project-changelog-ribbon-chevron" aria-hidden>
            {changelogOpen ? "▼" : "▶"}
          </span>
        </button>

        <div
          id="project-changelog-panel"
          className="project-changelog-panel"
          hidden={!changelogOpen}
        >
            <div className="project-changelog-block">
              <h3 className="project-changelog-subheading">Embedded change log</h3>
              <p className="project-detail-muted project-detail-changelog-hint">
                Historical field changes from imported project data and the board. Newest first.
              </p>
              {embeddedSorted.length === 0 ? (
                <p className="project-detail-muted">No embedded change_log entries yet.</p>
              ) : (
                <ul className="project-wp-changelog-list">
                  {embeddedSorted.map(({ entry, key, domain }) => {
                    const parsed = parseWpChangeFirstLine(entry.value);
                    return (
                      <li key={key} className="project-wp-changelog-card">
                        <div className="project-wp-changelog-card-head">
                          <span className="project-wp-changelog-date">
                            {entry.date != null ? String(entry.date) : "—"}
                          </span>
                          <span className="project-wp-changelog-user">
                            {entry.user != null ? String(entry.user) : "—"}
                          </span>
                          {domain ? (
                            <span className="project-wp-changelog-domain">{domain}</span>
                          ) : null}
                        </div>
                        <div className="project-wp-changelog-field">
                          {entry.field != null ? String(entry.field) : "—"}
                        </div>
                        {parsed.before != null && parsed.after != null ? (
                          <dl className="project-wp-changelog-fromto">
                            <dt>Before</dt>
                            <dd className="project-change-log-cell-value">{parsed.before || "—"}</dd>
                            <dt>After</dt>
                            <dd className="project-change-log-cell-value">{parsed.after || "—"}</dd>
                          </dl>
                        ) : (
                          <div className="project-wp-changelog-raw project-change-log-cell-value">
                            {parsed.detail || "—"}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="project-changelog-block">
              <h3 className="project-changelog-subheading">Database audit</h3>
              <p className="project-detail-muted project-detail-changelog-hint">
                Edits from the grid, imports, and migrations. Merged when you open this page.
              </p>
              {auditSorted.length === 0 ? (
                <p className="project-detail-muted">No audit rows for this project yet.</p>
              ) : (
                <div className="project-change-log-wrap">
                  <table className="project-change-log-table">
                    <thead>
                      <tr>
                        <th scope="col">When (UTC)</th>
                        <th scope="col">User</th>
                        <th scope="col">Field</th>
                        <th scope="col">From</th>
                        <th scope="col">To</th>
                        <th scope="col">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditSorted.map((row) => (
                        <tr key={row.id}>
                          <td className="project-change-log-cell-time">
                            {row.changed_at != null
                              ? String(row.changed_at).replace("T", " ").replace(/\.\d{3}Z?$/, "Z")
                              : "—"}
                          </td>
                          <td>{row.user_email != null ? String(row.user_email) : "—"}</td>
                          <td className="project-change-log-field-name">
                            {formatAuditCellValue(row.field_name)}
                          </td>
                          <td className="project-change-log-cell-value">{formatAuditCellValue(row.old_value)}</td>
                          <td className="project-change-log-cell-value">{formatAuditCellValue(row.new_value)}</td>
                          <td>{row.source != null ? String(row.source) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
        </div>
      </section>
          ) : null}
          {projectCommentsEnabled ? (
            <ProjectCommentsPanel
              projectId={id}
              projectTitle={
                [projectRow.work_order, projectRow.name].filter(Boolean).join(" · ") || ""
              }
              sectionClassName="project-detail-section project-comments-section project-detail-aux-card project-landing-footer-card"
              submitButtonClassName="project-comments-submit-btn"
            />
          ) : null}
        </div>
      ) : (
        <>
          {changeHistoryEnabled ? (
      <section
        className="project-detail-section project-changelog-section project-detail-section-card"
        aria-label="Change history"
      >
        <button
          type="button"
          className="project-changelog-ribbon"
          aria-expanded={changelogOpen}
          aria-controls="project-changelog-panel"
          onClick={() => setChangelogOpen((o) => !o)}
        >
          <span className="project-changelog-ribbon-accent" aria-hidden />
          <span className="project-changelog-ribbon-main">
            <span className="project-changelog-ribbon-title">Change history</span>
            <span className="project-changelog-ribbon-meta">
              {changelogTotal === 0
                ? "No entries yet"
                : `${changelogTotal} ${changelogTotal === 1 ? "entry" : "entries"} · newest first`}
            </span>
          </span>
          <span className="project-changelog-ribbon-chevron" aria-hidden>
            {changelogOpen ? "▼" : "▶"}
          </span>
        </button>

        <div
          id="project-changelog-panel"
          className="project-changelog-panel"
          hidden={!changelogOpen}
        >
            <div className="project-changelog-block">
              <h3 className="project-changelog-subheading">Embedded change log</h3>
              <p className="project-detail-muted project-detail-changelog-hint">
                Historical field changes from imported project data and the board. Newest first.
              </p>
              {embeddedSorted.length === 0 ? (
                <p className="project-detail-muted">No embedded change_log entries yet.</p>
              ) : (
                <ul className="project-wp-changelog-list">
                  {embeddedSorted.map(({ entry, key, domain }) => {
                    const parsed = parseWpChangeFirstLine(entry.value);
                    return (
                      <li key={key} className="project-wp-changelog-card">
                        <div className="project-wp-changelog-card-head">
                          <span className="project-wp-changelog-date">
                            {entry.date != null ? String(entry.date) : "—"}
                          </span>
                          <span className="project-wp-changelog-user">
                            {entry.user != null ? String(entry.user) : "—"}
                          </span>
                          {domain ? (
                            <span className="project-wp-changelog-domain">{domain}</span>
                          ) : null}
                        </div>
                        <div className="project-wp-changelog-field">
                          {entry.field != null ? String(entry.field) : "—"}
                        </div>
                        {parsed.before != null && parsed.after != null ? (
                          <dl className="project-wp-changelog-fromto">
                            <dt>Before</dt>
                            <dd className="project-change-log-cell-value">{parsed.before || "—"}</dd>
                            <dt>After</dt>
                            <dd className="project-change-log-cell-value">{parsed.after || "—"}</dd>
                          </dl>
                        ) : (
                          <div className="project-wp-changelog-raw project-change-log-cell-value">
                            {parsed.detail || "—"}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="project-changelog-block">
              <h3 className="project-changelog-subheading">Database audit</h3>
              <p className="project-detail-muted project-detail-changelog-hint">
                Edits from the grid, imports, and migrations. Merged when you open this page.
              </p>
              {auditSorted.length === 0 ? (
                <p className="project-detail-muted">No audit rows for this project yet.</p>
              ) : (
                <div className="project-change-log-wrap">
                  <table className="project-change-log-table">
                    <thead>
                      <tr>
                        <th scope="col">When (UTC)</th>
                        <th scope="col">User</th>
                        <th scope="col">Field</th>
                        <th scope="col">From</th>
                        <th scope="col">To</th>
                        <th scope="col">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditSorted.map((row) => (
                        <tr key={row.id}>
                          <td className="project-change-log-cell-time">
                            {row.changed_at != null
                              ? String(row.changed_at).replace("T", " ").replace(/\.\d{3}Z?$/, "Z")
                              : "—"}
                          </td>
                          <td>{row.user_email != null ? String(row.user_email) : "—"}</td>
                          <td className="project-change-log-field-name">
                            {formatAuditCellValue(row.field_name)}
                          </td>
                          <td className="project-change-log-cell-value">{formatAuditCellValue(row.old_value)}</td>
                          <td className="project-change-log-cell-value">{formatAuditCellValue(row.new_value)}</td>
                          <td>{row.source != null ? String(row.source) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
        </div>
      </section>
          ) : null}
          {projectCommentsEnabled ? (
            <ProjectCommentsPanel
              projectId={id}
              projectTitle={
                [projectRow.work_order, projectRow.name].filter(Boolean).join(" · ") || ""
              }
            />
          ) : null}
        </>
      )}
    </>
  );

  return (
    <div className={shellClass}>
      <style>{PROJECT_LANDING_STYLES}</style>
      <div className="project-detail-landing-inner">{pageContent}</div>
      {phaseEmailModal ? (
        <PhaseCompleteEmailModal
          title={`${phaseEmailModal.cur} Complete — review email`}
          loading={phaseEmailPreviewLoading}
          loadError={phaseEmailPreviewError}
          preview={phaseEmailPreview}
          sending={phaseEmailSending}
          sendError={phaseEmailSendError}
          onCancel={closePhaseEmailModal}
          onSend={onPhaseEmailModalSend}
          onCompleteWithoutEmail={onPhaseEmailModalCompleteWithoutEmail}
        />
      ) : null}
    </div>
  );
}
